// src/cashup/StaffDashboardTab.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc, collection, deleteDoc, doc, onSnapshot, getDocs, getDoc,
  updateDoc, serverTimestamp, setDoc, query, where, writeBatch, limit
} from 'firebase/firestore';
import { db, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { useOrgId } from '../utils/tenant';
import Confirm from '../components/Confirm';
import { toast } from 'react-toastify';
import { logAudit } from '../utils/audit';
import useActor from '../utils/useActor';
import NeoMorpheusDutyDialog from './NeoMorpheusStandards';
const publishMorpheusSection = httpsCallable(functions, 'neoMorpheus');

const FIELD_TYPES = ['text', 'number', 'checkbox', 'photo'];
const TABS = ['overview', 'builder', 'response', 'performance', 'settings', 'timeline'];

const emptyResponseRule = () => ({
  active: false,
  dueTime: '',
  remindBeforeMins: '',
  repeatEveryMins: '',
  escalateAfterMins: '',
  escalationMethod: 'dashboard',
  escalateTo: '',
  escalationTargetType: 'role',
  escalationRole: 'shift_lead',
  escalationLevel2Enabled: false,
  escalationLevel2AfterMins: '',
  escalationLevel2Method: 'dashboard',
  escalationLevel2To: '',
  escalationLevel2TargetType: 'role',
  escalationLevel2Role: 'manager',
  criticalDuty: false,
  escalationLevel3Enabled: false,
  escalationLevel3AfterMins: '',
  escalationLevel3Method: 'dashboard',
  escalationLevel3To: '',
  escalationLevel3TargetType: 'role',
  escalationLevel3Role: 'owner',
});

const normalizeResponseRule = (rule = {}) => ({ ...emptyResponseRule(), ...(rule || {}) });
const responseRuleHasSettings = (rule = {}) => {
  const r = normalizeResponseRule(rule);
  return !!(
    r.active ||
    r.dueTime ||
    r.remindBeforeMins ||
    r.repeatEveryMins ||
    r.escalateAfterMins ||
    r.escalationMethod !== 'dashboard' ||
    r.escalateTo ||
    r.escalationTargetType !== 'role' ||
    r.escalationRole !== 'shift_lead' ||
    r.escalationLevel2Enabled ||
    r.escalationLevel2AfterMins ||
    r.escalationLevel2Method !== 'dashboard' ||
    r.escalationLevel2To ||
    r.escalationLevel2TargetType !== 'role' ||
    r.escalationLevel2Role !== 'manager' ||
    r.criticalDuty ||
    r.escalationLevel3Enabled ||
    r.escalationLevel3AfterMins ||
    r.escalationLevel3Method !== 'dashboard' ||
    r.escalationLevel3To ||
    r.escalationLevel3TargetType !== 'role' ||
    r.escalationLevel3Role !== 'owner'
  );
};
const inputStyle = { backgroundColor: 'rgba(0,0,0,0.35)', color: '#fff', colorScheme: 'dark' };
const darkSelectOptionStyle = { backgroundColor: '#0b1627', color: '#fff' };

const normalizeEscalationMethodValue = (value = '') => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'sms') return 'whatsapp';
  if (raw === 'email + sms' || raw === 'email+sms') return 'both';
  return raw || 'email';
};

const escalationMethodLabel = (value = '') => {
  const method = normalizeEscalationMethodValue(value);
  if (method === 'whatsapp') return 'WhatsApp';
  if (method === 'both') return 'Email + WhatsApp';
  if (method === 'dashboard') return 'Manager Dashboard';
  if (method === 'push') return 'Push notification';
  if (method === 'multi') return 'Dashboard + Push';
  return 'Email';
};

const slugifyDutyId = (value = 'duty') => String(value || 'duty')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 64) || 'duty';

const cleanPublishedRule = (rule = {}) => {
  const r = normalizeResponseRule(rule);
  const graceMins = Number(r.escalateAfterMins || 0);
  const requestedRepeatMins = Number(r.repeatEveryMins || 0);
  const repeatEveryMins =
    graceMins > 1 && requestedRepeatMins > 0
      ? String(Math.min(requestedRepeatMins, graceMins - 1))
      : '';
  return {
    ...r,
    dueTime: String(r.dueTime || '').trim(),
    remindBeforeMins: r.remindBeforeMins === '' ? '' : String(r.remindBeforeMins),
    repeatEveryMins,
    escalateAfterMins: r.escalateAfterMins === '' ? '' : String(r.escalateAfterMins),
    escalationMethod: r.escalationMethod || 'dashboard',
    escalateTo: r.escalateTo || '',
    escalationTargetType: r.escalationTargetType || 'role',
    escalationRole: r.escalationRole || 'shift_lead',
    escalationLevel2Enabled: !!r.escalationLevel2Enabled,
    escalationLevel2AfterMins: r.escalationLevel2AfterMins === '' ? '' : String(r.escalationLevel2AfterMins),
    escalationLevel2Method: r.escalationLevel2Method || 'dashboard',
    escalationLevel2To: r.escalationLevel2To || '',
    escalationLevel2TargetType: r.escalationLevel2TargetType || 'role',
    escalationLevel2Role: r.escalationLevel2Role || 'manager',
    criticalDuty: !!r.criticalDuty,
    escalationLevel3Enabled: !!r.escalationLevel3Enabled,
    escalationLevel3AfterMins: r.escalationLevel3AfterMins === '' ? '' : String(r.escalationLevel3AfterMins),
    escalationLevel3Method: r.escalationLevel3Method || 'dashboard',
    escalationLevel3To: r.escalationLevel3To || '',
    escalationLevel3TargetType: r.escalationLevel3TargetType || 'role',
    escalationLevel3Role: r.escalationLevel3Role || 'owner',
  };
};

const emptyAggregateResponseTarget = () => ({
  active: false,
  dueTime: '',
  remindBeforeMins: '',
  repeatEveryMins: '',
  escalateAfterMins: '',
  ownerRoleId: '',
  ownerRoleName: '',
});

const normalizeAggregateResponseTarget = (target = {}) => ({
  ...emptyAggregateResponseTarget(),
  ...(target || {}),
});

const cleanAggregateResponseTarget = (target = {}, defaults = {}) => {
  const t = normalizeAggregateResponseTarget(target);
  const rule = cleanPublishedRule({
    ...emptyResponseRule(),
    active: !!t.active,
    dueTime: String(t.dueTime || '').trim(),
    remindBeforeMins:
      t.remindBeforeMins === ''
        ? String(defaults.reminderBeforeMins || '')
        : String(t.remindBeforeMins),
    repeatEveryMins:
      t.repeatEveryMins === ''
        ? String(defaults.repeatEveryMins || '')
        : String(t.repeatEveryMins),
    escalateAfterMins:
      t.escalateAfterMins === ''
        ? String(defaults.gracePeriodMins || defaults.escalateAfterMins || '')
        : String(t.escalateAfterMins),
    escalationMethod: defaults.escalationMethod || 'dashboard',
    escalateTo: defaults.escalateTo || '',
    escalationTargetType: defaults.escalationTargetType || 'role',
    escalationRole: defaults.escalationRole || 'shift_lead',
  });
  return {
    ...t,
    active: !!t.active,
    dueTime: String(t.dueTime || '').trim(),
    ownerRoleId: String(t.ownerRoleId || '').trim(),
    ownerRoleName: String(t.ownerRoleName || '').trim(),
    responseRule: rule,
  };
};

const DRAFT_DB_NAME = 'neo_staff_dashboard_drafts_v1';
const DRAFT_STORE_NAME = 'drafts';

function openDraftDb() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    const request = window.indexedDB.open(DRAFT_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(DRAFT_STORE_NAME)) {
        database.createObjectStore(DRAFT_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Could not open draft database'));
  });
}

async function draftDbGet(key) {
  const database = await openDraftDb();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(DRAFT_STORE_NAME, 'readonly');
    const store = tx.objectStore(DRAFT_STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error || new Error('Could not read draft'));
    tx.oncomplete = () => database.close();
    tx.onerror = () => {
      database.close();
      reject(tx.error || new Error('Could not read draft'));
    };
  });
}

async function draftDbSet(key, value) {
  const database = await openDraftDb();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(DRAFT_STORE_NAME, 'readwrite');
    const store = tx.objectStore(DRAFT_STORE_NAME);
    store.put(value, key);
    tx.oncomplete = () => {
      database.close();
      resolve(true);
    };
    tx.onerror = () => {
      database.close();
      reject(tx.error || new Error('Could not save draft'));
    };
  });
}

async function draftDbDelete(key) {
  const database = await openDraftDb();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(DRAFT_STORE_NAME, 'readwrite');
    const store = tx.objectStore(DRAFT_STORE_NAME);
    store.delete(key);
    tx.oncomplete = () => {
      database.close();
      resolve(true);
    };
    tx.onerror = () => {
      database.close();
      reject(tx.error || new Error('Could not delete draft'));
    };
  });
}

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

function minutesFromTime(time = '') {
  const [h, m] = String(time || '').split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return (h * 60) + m;
}

function currentMinutes() {
  const now = new Date();
  return (now.getHours() * 60) + now.getMinutes();
}

const toSec = (v) => v?.seconds ?? (v?.toDate ? Math.floor(v.toDate().getTime() / 1000) : (typeof v === 'number' ? v : null));

function formatHMFromMinutes(total) {
  if (total == null) return '—';
  const safe = ((total % 1440) + 1440) % 1440;
  const h = String(Math.floor(safe / 60)).padStart(2, '0');
  const m = String(safe % 60).padStart(2, '0');
  return `${h}:${m}`;
}

function timelineStepsForRule(rule) {
  const normalized = normalizeResponseRule(rule);
  const due = minutesFromTime(normalized.dueTime);
  if (!normalized.active || due === null) return [];
  const now = currentMinutes();
  const remind = Math.max(0, Number(normalized.remindBeforeMins || 0));
  const repeat = Math.max(0, Number(normalized.repeatEveryMins || 0));
  const escalate = Math.max(0, Number(normalized.escalateAfterMins || 20));
  const reminderAt = due - remind;
  const repeatAt = repeat ? due + repeat : null;
  const escalateAt = due + escalate;
  const mk = (label, at, detail) => ({ label, at: formatHMFromMinutes(at), detail, state: now >= at ? 'triggered' : 'pending' });
  return [
    mk('Reminder', reminderAt, remind ? `${remind}m before due` : 'At due time'),
    mk('Due', due, 'Duty should be completed'),
    ...(repeatAt ? [mk('Repeat reminder', repeatAt, `Every ${repeat}m while still incomplete`)] : []),
    mk('Escalation', escalateAt, `${escalate}m after due if incomplete`),
  ];
}


function toMillis(v) {
  if (!v) return null;
  if (typeof v === 'number') return v > 9999999999 ? v : v * 1000;
  if (v?.toDate) return v.toDate().getTime();
  if (v?.seconds) return v.seconds * 1000;
  const parsed = Date.parse(v);
  return Number.isNaN(parsed) ? null : parsed;
}

function formatTimeValue(v) {
  const ms = toMillis(v);
  if (!ms) return '—';
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function minutesBetweenNow(v) {
  const ms = toMillis(v);
  if (!ms) return null;
  return Math.max(0, Math.round((Date.now() - ms) / 60000));
}

function queueItemStatus(item = {}) {
  if (item.completed || item.status === 'completed' || item.lifecycleStatus === 'completed') return 'resolved';
  if (item.managerAcknowledged || item.managerStatus === 'acknowledged') return 'acknowledged';

  const lifecycle = String(item.lifecycleStatus || '');
  if (lifecycle === 'escalated') return 'escalated';
  if (lifecycle === 'overdue' || lifecycle === 'owner_unavailable' || lifecycle === 'awaiting_replacement') return 'overdue';
  if (lifecycle === 'due' || lifecycle === 'reminder_sent' || lifecycle === 'reassigned') return 'due';
  if (lifecycle === 'upcoming') return 'upcoming';

  if (item.escalationStatus === 'ready' || item.escalationReady || item.status === 'escalated') return 'escalated';
  if (item.status === 'overdue') return 'overdue';
  const dueMs = toMillis(item.dueAt);
  const escMs = toMillis(item.escalationAt);
  if (escMs && Date.now() >= escMs) return 'escalated';
  if (dueMs && Date.now() >= dueMs) return 'due';
  return 'upcoming';
}


function managerResponseIntel(item = {}) {
  const lifecycle = String(item.lifecycleStatus || '');
  const assurance = String(item.executionAssuranceStatus || '');
  const status = item.computedStatus || queueItemStatus(item);
  const owner = item.assignedStaffName || item.responseOwner || item.ownerName || '';
  const plannedOwner = item.plannedOwnerStaffName || '';
  const overdueMins = minutesBetweenNow(item.dueAt);

  const attendance = String(item.attendanceStatus || '');

  if (item.managerAttentionRequired === true || assurance === 'attention_required') {
    const type = String(item.managerAttentionType || '');
    const byType = {
      attendance_gap: {
        severity: 'red',
        headline: 'Expected duty owner is not on shift',
        action: 'Review attendance or arrange cover',
      },
      coverage_gap: {
        severity: 'red',
        headline: 'Duty coverage lost',
        action: 'Arrange cover',
      },
      missing_owner: {
        severity: 'red',
        headline: 'No available owner',
        action: 'Arrange cover',
      },
      escalated: {
        severity: 'red',
        headline: 'Manager intervention required',
        action: 'Acknowledge & take ownership',
      },
      overdue: {
        severity: 'orange',
        headline: 'Duty overdue',
        action: 'Review staff response',
      },
    };
    const config = byType[type] || {
      severity: 'orange',
      headline: 'Management attention required',
      action: 'Review exception',
    };
    return {
      category: type || 'attention',
      needsManager: true,
      severity: config.severity,
      headline: config.headline,
      action: config.action,
      explanation:
        item.executionAssuranceReason ||
        item.attendanceReason ||
        item.lifecycleReason ||
        'Neo detected an exception requiring management.',
    };
  }

  if (assurance === 'handled_by_neo') {
    return {
      category: 'handled_by_neo',
      needsManager: false,
      severity: 'blue',
      headline: 'Neo handling automatically',
      action: 'No manager action',
      explanation: item.executionAssuranceReason || 'Neo is handling this duty automatically.',
    };
  }

  if (assurance === 'healthy') {
    return {
      category: 'healthy',
      needsManager: false,
      severity: 'green',
      headline: 'Operations normal',
      action: 'No action required',
      explanation: item.executionAssuranceReason || 'No intervention required.',
    };
  }

  if (
    attendance === 'expected_owner_not_clocked_in' &&
    !item.attendanceReplacementActive &&
    !['reassigned', 'completed'].includes(lifecycle)
  ) {
    return {
      category: 'attendance_gap',
      needsManager: true,
      severity: 'red',
      headline: 'Expected duty owner is not on shift',
      action: 'Review attendance or arrange cover',
      explanation: item.attendanceReason || `${plannedOwner || 'Expected owner'} is not clocked in.`,
    };
  }

  if (attendance === 'owner_left_before_due' && !item.attendanceReplacementActive) {
    return {
      category: 'attendance_gap',
      needsManager: true,
      severity: 'red',
      headline: 'Duty owner left before duty time',
      action: 'Arrange cover',
      explanation: item.attendanceReason || 'The assigned staff member clocked out before this duty became due.',
    };
  }

  if (attendance === 'covered_by_replacement') {
    return {
      category: 'attendance_covered',
      needsManager: false,
      severity: 'blue',
      headline: 'Attendance gap covered',
      action: 'Monitor only',
      explanation: item.attendanceReason || 'A live replacement is on shift and owns this duty.',
    };
  }

  if (status === 'resolved') {
    return {
      category: 'resolved',
      needsManager: false,
      severity: 'green',
      headline: 'Resolved',
      action: 'No action required',
      explanation: item.completedBy
        ? `Completed by ${item.completedBy}.`
        : 'Duty is complete.',
    };
  }

  if (lifecycle === 'owner_unavailable' || lifecycle === 'awaiting_replacement') {
    return {
      category: 'coverage',
      needsManager: true,
      severity: 'red',
      headline: 'No available owner',
      action: 'Arrange cover',
      explanation: item.resolvedOwnerRoleName || item.ownerRoleName
        ? `${item.resolvedOwnerRoleName || item.ownerRoleName} coverage is missing${plannedOwner ? ` · expected ${plannedOwner}` : ''}.`
        : `No eligible staff member is available${plannedOwner ? ` · expected ${plannedOwner}` : ''}.`,
    };
  }

  if (lifecycle === 'escalated' || status === 'escalated') {
    return {
      category: 'escalation',
      needsManager: true,
      severity: 'red',
      headline: 'Manager intervention required',
      action: 'Acknowledge & take ownership',
      explanation: overdueMins != null
        ? `Duty remains unresolved · ${overdueMins}m past due.`
        : 'Duty remains unresolved after escalation.',
    };
  }

  if (lifecycle === 'overdue' || status === 'overdue') {
    return {
      category: 'overdue',
      needsManager: true,
      severity: 'orange',
      headline: 'Duty overdue',
      action: 'Review staff response',
      explanation: `${owner ? `${owner} owns this duty` : 'Duty has an owner'}${overdueMins != null ? ` · ${overdueMins}m overdue` : ''}.`,
    };
  }

  if (lifecycle === 'reassigned') {
    return {
      category: 'reassigned',
      needsManager: false,
      severity: 'blue',
      headline: 'Coverage restored automatically',
      action: 'Monitor only',
      explanation:
        item.reassignedFromStaffName && item.assignedStaffName
          ? `${item.reassignedFromStaffName} → ${item.assignedStaffName}`
          : owner
          ? `Neo reassigned the duty to ${owner}.`
          : 'Neo resolved replacement coverage.',
    };
  }

  if (['due', 'reminder_sent'].includes(lifecycle) || status === 'due') {
    return {
      category: 'staff_response',
      needsManager: false,
      severity: 'blue',
      headline: 'Staff response in progress',
      action: 'No manager action yet',
      explanation: owner
        ? `${owner} is responsible${item.staffReminderStatus ? ` · WhatsApp ${String(item.staffReminderStatus).replaceAll('_', ' ')}` : ''}.`
        : 'Neo is handling the staff response flow.',
    };
  }

  return {
    category: 'normal',
    needsManager: false,
    severity: 'green',
    headline: 'Operations normal',
    action: 'No action required',
    explanation: 'Neo is handling this duty without manager intervention.',
  };
}

function activeStaffNamesForStore(rows, selectedStore) {
  const names = (rows || [])
    .filter(r => !selectedStore?.id || r.storeId === selectedStore.id || r.storeName === selectedStore.name)
    .map(r => r.name)
    .filter(Boolean);
  return Array.from(new Set(names));
}

function riskStatusForRule(rule) {
  const normalized = normalizeResponseRule(rule);
  if (!normalized.active || !normalized.dueTime) return null;
  const due = minutesFromTime(normalized.dueTime);
  if (due === null) return null;
  const now = currentMinutes();
  const lateBy = now - due;
  const remindBefore = Number(normalized.remindBeforeMins || 0);
  const escalateAfter = Number(normalized.escalateAfterMins || 20);
  if (lateBy >= escalateAfter) return { status: 'Escalated', tone: 'red', timeText: `${lateBy}m late` };
  if (lateBy > 0) return { status: 'Delayed', tone: 'orange', timeText: `${lateBy}m late` };
  if (lateBy === 0) return { status: 'Due Now', tone: 'blue', timeText: 'due now' };
  if (due - now <= Math.max(remindBefore, 60)) return { status: 'Due Soon', tone: 'blue', timeText: `due in ${Math.max(due - now, 0)}m` };
  return { status: 'Scheduled', tone: 'green', timeText: normalized.dueTime };
}

// Duty owner + staff WhatsApp routing is server-owned. Frontend only displays the resolved state.
const SERVER_DUTY_ROUTING_AUTHORITY = true;

export default function StaffDashboardTab({ siteScope = { allSites: true, siteIds: [] } }) {
  const orgId = useOrgId();
  const actor = useActor();

  const [activeTab, setActiveTab] = useState('overview');
  const [stores, setStores] = useState([]);
  const [roles, setRoles] = useState([]);
  const [staffDirectory, setStaffDirectory] = useState([]);
  const [activeStaffRows, setActiveStaffRows] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [sections, setSections] = useState([]);
  const [newSec, setNewSec] = useState({ name: '', subheading: '', fieldLabel: '', fieldType: '' });
  const [applyAllStores, setApplyAllStores] = useState(false);
  const [dailyMessage, setDailyMessage] = useState('');
  const [messageApplyAll, setMessageApplyAll] = useState(true);
  const [msgSaved, setMsgSaved] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [dedupeOpen, setDedupeOpen] = useState(false);
  const [banner, setBanner] = useState('');
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [overviewRange, setOverviewRange] = useState('today');
  const [responseCentreItems, setResponseCentreItems] = useState([]);
  const [responseNotifications, setResponseNotifications] = useState([]);
  const [responseSettings, setResponseSettings] = useState({
    defaultEscalationEmail: '',
    smsEnabled: false,
    defaultRepeatEveryMins: '10',
    defaultEscalateAfterMins: '20',
    quietHoursStart: '',
    quietHoursEnd: '',
    reminderTemplate: 'Reminder: {duty} is due at {time}.',
    delayedTemplate: 'Delayed: {duty} is now overdue at {site}.',
    escalatedTemplate: 'Escalated: {duty} is still not done at {site}.',
    // N1 Sprint 4.1 - live Duty Due WhatsApp channel.
    whatsappDutyDueEnabled: true,
    whatsappDutyDueTemplate: 'neo_duty_due',
    whatsappDutyDueLanguage: 'en',
    neoStaffAppUrl: 'https://neo.matrixos.co.za',
    // Sprint 4.3 Phase 3 - management exception routing.
    whatsappManagerEscalationEnabled: true,
    whatsappManagerEscalationTemplate: 'neo_manager_escalation',
    whatsappManagerEscalationLanguage: 'en',
    managerEscalationAfterMins: 30,
    neoAdminUrl: 'https://neo.matrixos.co.za',
  });

  useEffect(() => {
    if (!orgId) return undefined;
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'cashup_stores'), snap => {
      const allowedSites = new Set(siteScope.siteIds || []);
      const list = snap.docs
        .map(d => ({ id: d.id, ...(d.data() || {}) }))
        .filter(s => siteScope.allSites || allowedSites.has(s.id))
        .filter(s => !(s?.isArchived === true || s?.archived === true))
        .sort((a, b) => String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase()));
      setStores(list);
      if (!storeId && list[0]) setStoreId(list[0].id);
    });
    return unsub;
  }, [orgId, storeId, siteScope.allSites, siteScope.siteIds]);

  useEffect(() => {
    if (!orgId) return undefined;
    return onSnapshot(collection(db, 'orgs', orgId, 'cashup_roles'), snap => {
      setRoles(snap.docs
        .map(d => ({ id: d.id, ...(d.data() || {}) }))
        .filter(r => !(r?.isArchived === true || r?.archived === true))
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));
    });
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return undefined;
    return onSnapshot(collection(db, 'orgs', orgId, 'cashup_users'), snap => {
      setStaffDirectory(snap.docs
        .map(d => ({ id: d.id, ...(d.data() || {}) }))
        .filter(person => !(person?.isArchived === true || person?.archived === true)));
    });
  }, [orgId]);

  useEffect(() => {
    if (!orgId || stores.length === 0) return undefined;
    let cancelled = false;
    const loadActiveStaff = async () => {
      try {
        const recentMs = Date.now() - (18 * 60 * 60 * 1000);
        const rows = [];
        const storeMap = Object.fromEntries(stores.map(s => [s.id, s.name]));
        await Promise.all(stores.map(async (s) => {
          try {
            const snap = await getDocs(collection(db, 'orgs', orgId, 'stores', s.id, 'shifts'));
            snap.docs.forEach(d => {
              const data = d.data() || {};
              const ci = toSec(data.clockIn) ?? toSec(data.clockInAt) ?? toSec(data.in) ?? toSec(data.inAt);
              const co = toSec(data.clockOut) ?? toSec(data.clockOutAt) ?? toSec(data.out) ?? toSec(data.outAt);
              if (ci && !co && ci * 1000 >= recentMs) rows.push({ id: d.id, staffId: data.staffId || data.userId || data.uid || '', name: data.name || data.staffName || '', role: data.role || data.roleName || '', storeId: s.id, storeName: s.name, clockInSec: ci });
            });
          } catch (e) {}
        }));
        try {
          const legacy = await getDocs(query(collection(db, 'cashup_shifts'), where('orgId', '==', orgId)));
          legacy.docs.forEach(d => {
            const data = d.data() || {};
            const ci = toSec(data.clockIn) ?? toSec(data.clockInAt) ?? toSec(data.in) ?? toSec(data.inAt);
            const co = toSec(data.clockOut) ?? toSec(data.clockOutAt) ?? toSec(data.out) ?? toSec(data.outAt);
            const rawStore = data.storeId || data.store || '';
            const store = stores.find(s => s.id === rawStore || s.name === rawStore);
            if (ci && !co && ci * 1000 >= recentMs) rows.push({ id: d.id, staffId: data.staffId || data.userId || data.uid || '', name: data.name || data.staffName || '', role: data.role || data.roleName || '', storeId: store?.id || rawStore, storeName: store?.name || storeMap[rawStore] || rawStore, clockInSec: ci });
          });
        } catch (e) {}
        const byKey = new Map();
        rows.forEach(r => {
          const key = `${String(r.name || '').trim().toLowerCase()}|${r.storeId || r.storeName || ''}`;
          const prev = byKey.get(key);
          if (!prev || r.clockInSec > prev.clockInSec) byKey.set(key, r);
        });
        if (!cancelled) setActiveStaffRows(Array.from(byKey.values()).sort((a, b) => b.clockInSec - a.clockInSec));
      } catch (e) {
        if (!cancelled) setActiveStaffRows([]);
      }
    };
    loadActiveStaff();
    const t = setInterval(loadActiveStaff, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [orgId, stores]);

  useEffect(() => {
    if (!orgId) return undefined;
    const settingsRef = doc(db, 'orgs', orgId, 'neo_settings', 'staffDashboardResponse');
    const unsub = onSnapshot(settingsRef, snap => {
      const data = snap.data() || {};
      setResponseSettings(s => ({
        ...s,
        ...data,
        whatsappDutyDueEnabled:
          typeof data.whatsappDutyDueEnabled === 'boolean'
            ? data.whatsappDutyDueEnabled
            : true,
      }));

      // Existing orgs created before the Duty Due channel had no flag at all.
      // Backfill only the missing value; an explicit admin "false" is respected.
      if (snap.exists() && typeof data.whatsappDutyDueEnabled !== 'boolean') {
        setDoc(settingsRef, { whatsappDutyDueEnabled: true }, { merge: true })
          .catch(error => console.error('Could not enable Neo duty WhatsApp default', error));
      }
    });
    return unsub;
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return undefined;
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'neoDutyQueue'), snap => {
      const rows = snap.docs.map(d => {
        const data = d.data() || {};
        return { id: d.id, ...data, computedStatus: queueItemStatus(data) };
      }).sort((a, b) => (toMillis(a.dueAt) || 0) - (toMillis(b.dueAt) || 0));
      setResponseCentreItems(rows);
    }, err => {
      console.error('Could not load Neo response queue', err);
      setResponseCentreItems([]);
    });
    return unsub;
  }, [orgId]);

  // S7 integration hardening: resolve the operational duty owner from the same
  // Staff master + live attendance data used by Neo Admin. This makes ownership
  // persistent before the reminder/escalation workers act, rather than depending
  // on Schedule Intelligence being open in the browser.
  useEffect(() => {
    if (SERVER_DUTY_ROUTING_AUTHORITY) return;
    if (!orgId || !responseCentreItems.length || !activeStaffRows.length || !staffDirectory.length) return;

    const norm = value => String(value || '').trim().toLowerCase();
    const roleForPerson = person => {
      const role = person?.roleId ? roles.find(r => String(r.id) === String(person.roleId)) : null;
      return { id: String(person?.roleId || ''), name: String(role?.name || person?.roleName || person?.role || '').trim() };
    };
    const roleForActiveShift = (shift, person) => {
      const shiftRoleName = String(shift?.role || shift?.roleName || '').trim();
      const shiftRole = shiftRoleName
        ? roles.find(r => norm(r.name) === norm(shiftRoleName))
        : null;

      // S7 contract: the role chosen for the ACTIVE shift is the operational
      // authority. Staff master role is only the fallback identity/default.
      if (shiftRoleName) {
        return {
          id: String(shiftRole?.id || ''),
          name: String(shiftRole?.name || shiftRoleName),
        };
      }
      return roleForPerson(person);
    };
    const personForShift = shift =>
      (shift.staffId && staffDirectory.find(p => String(p.id) === String(shift.staffId))) ||
      staffDirectory.find(p => norm(p.name) === norm(shift.name)) || null;

    const resolve = async () => {
      const batch = writeBatch(db);
      let writes = 0;
      responseCentreItems.forEach(item => {
        if (item.active === false || item.archived || item.deleted || item.completed || item.completedAt || item.resolved || item.resolvedAt) return;

        const requiredRoleId = String(item.ownerRoleId || '');
        const requiredRoleName = norm(item.ownerRoleName);
        const roleOwned = item.ownershipMode === 'scheduled_role' || !!requiredRoleId || !!requiredRoleName;
        const dutyStoreId = String(item.storeId || '');
        const dutyStoreName = norm(item.storeName || item.siteName);

        const eligible = activeStaffRows
          .map(shift => ({ shift, person: personForShift(shift) }))
          .filter(({ shift }) => {
            if (dutyStoreId) return String(shift.storeId || '') === dutyStoreId;
            return !dutyStoreName || norm(shift.storeName) === dutyStoreName;
          })
          .filter(({ shift, person }) => {
            if (!roleOwned) return true;
            const resolved = roleForActiveShift(shift, person);

            // Older duties often stored a role ID while older shifts stored only
            // the role label. Match by ID when both are available, otherwise use
            // the role name so a live Cashier can never become "Unassigned"
            // merely because one side lacks the modern role ID.
            if (requiredRoleId && resolved.id) return resolved.id === requiredRoleId;
            const requiredNameFromId = requiredRoleId
              ? roles.find(r => String(r.id) === requiredRoleId)?.name || ''
              : '';
            return norm(resolved.name) === norm(requiredRoleName || requiredNameFromId);
          })
          .sort((a, b) => Number(a.shift.clockInSec || 0) - Number(b.shift.clockInSec || 0));

        const match = eligible[0];
        if (!match) return;
        const nextId = String(match.person?.id || match.shift.staffId || '');
        const nextName = String(match.person?.name || match.shift.name || '');
        if (!nextId && !nextName) return;

        if (String(item.assignedStaffId || item.responseOwnerUid || '') === nextId &&
            norm(item.assignedStaffName || item.responseOwner) === norm(nextName)) return;

        const resolvedRole = roleForActiveShift(match.shift, match.person);
        batch.set(doc(db, 'orgs', orgId, 'neoDutyQueue', item.id), {
          assignedStaffId: nextId,
          assignedStaffName: nextName,
          responseOwnerUid: nextId,
          responseOwner: nextName,
          resolvedOwnerRoleId: resolvedRole.id || requiredRoleId,
          resolvedOwnerRoleName: resolvedRole.name || item.ownerRoleName || '',
          ownershipResolutionStatus: 'available',
          ownershipResolvedFrom: 'staff_master_live_attendance',
          ownershipActualShiftId: match.shift.id || '',
          ownershipAvailabilityChecked: true,
          ownershipResolvedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }, { merge: true });
        writes += 1;
      });
      if (writes) await batch.commit();
    };
    resolve().catch(error => console.error('S7 duty owner resolution failed', error));
  }, [orgId, responseCentreItems, activeStaffRows, staffDirectory, roles]);

  // S7.6 hardening: queue the staff WhatsApp reminder from the Response Centre too.
  // The operational pipeline must not depend on a manager having Schedule Intelligence open.
  useEffect(() => {
    if (SERVER_DUTY_ROUTING_AUTHORITY) return;
    if (!orgId || !responseCentreItems.length || !staffDirectory.length) return;
    const toMsSafe = value => toMillis(value) || 0;
    const normalizePhone = value => {
      const raw = String(value || '').trim();
      const digits = raw.replace(/\D/g, '');
      if (!digits) return '';
      if (digits.startsWith('27')) return `+${digits}`;
      if (digits.startsWith('0')) return `+27${digits.slice(1)}`;
      return raw.startsWith('+') ? `+${digits}` : `+${digits}`;
    };
    const norm = value => String(value || '').trim().toLowerCase();
    const nowMs = Date.now();

    const queue = async () => {
      if (responseSettings.whatsappDutyDueEnabled === false) return;
      const batch = writeBatch(db);
      let writes = 0;
      responseCentreItems.forEach(item => {
        if (item.active === false || item.archived || item.deleted || item.completed || item.completedAt || item.resolved || item.resolvedAt) return;
        const staffId = String(item.assignedStaffId || item.responseOwnerUid || '');
        const staffName = String(item.assignedStaffName || item.responseOwner || '');
        if (!staffId && !staffName) return;

        const dueMs = toMsSafe(item.dueAt);
        if (!dueMs) return;
        const rule = item.responseRule || {};
        const remindBefore = Math.max(0, Number(item.reminderBeforeMins ?? rule.remindBeforeMins ?? 0) || 0);
        const reminderMs = dueMs - remindBefore * 60000;
        if (nowMs < reminderMs) return;

        const person =
          (staffId && staffDirectory.find(p => String(p.id) === staffId)) ||
          staffDirectory.find(p => norm(p.name) === norm(staffName));
        if (!person) return;
        const identity = person.responseIdentity || {};
        const whatsapp = normalizePhone(person.whatsappNumber || identity.whatsappNumber || identity.whatsapp || '');
        const preferred = norm(person.preferredResponseChannel || identity.preferredChannel || 'whatsapp');
        if (!whatsapp || (preferred && !['whatsapp', 'both'].includes(preferred))) return;

        const phase = nowMs < dueMs ? 'pre_due' : 'due';
        const slotMs = phase === 'pre_due' ? reminderMs : dueMs;
        const template = String(responseSettings.whatsappDutyDueTemplate || 'neo_duty_due');
        const fingerprint = ['neo_duty_reminder', item.id, staffId || staffName, phase, slotMs, template].join('|');
        const previous = Array.isArray(item.staffReminderFingerprints) ? item.staffReminderFingerprints : [];
        if (item.staffReminderFingerprint === fingerprint || previous.includes(fingerprint)) return;

        batch.set(doc(db, 'orgs', orgId, 'neoDutyQueue', item.id), {
          staffReminderStatus: 'queued',
          staffReminderFingerprint: fingerprint,
          staffReminderFingerprints: [...previous, fingerprint].slice(-30),
          staffReminderPhase: phase,
          staffReminderSequence: 0,
          staffReminderSlotMs: slotMs,
          staffReminderQueuedAt: serverTimestamp(),
          staffReminderRecipientId: staffId || person.id || '',
          staffReminderRecipientName: staffName || person.name || '',
          staffReminderWhatsapp: whatsapp,
          staffReminderTemplate: template,
          staffReminderLanguage: String(responseSettings.whatsappDutyDueLanguage || 'en'),
          staffReminderError: '',
          staffReminder: {
            status: 'queued',
            fingerprint,
            phase,
            sequence: 0,
            slotMs,
            reason: phase === 'pre_due' ? `${remindBefore}m before due` : 'Duty due',
            channel: 'whatsapp',
            template,
            language: String(responseSettings.whatsappDutyDueLanguage || 'en'),
            recipientId: staffId || person.id || '',
            recipientName: staffName || person.name || '',
            whatsappNumber: whatsapp,
            dutyLabel: String(item.dutyLabel || item.label || item.dutyName || 'Duty'),
            storeName: String(item.storeName || item.siteName || ''),
            dueTime: new Date(dueMs).toLocaleTimeString('en-ZA', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }),
            error: '',
          },
          responseRequestedAt: serverTimestamp(),
          responseRequestedBy: 'staff_dashboard_s7_hardening',
          updatedAt: serverTimestamp(),
        }, { merge: true });
        writes += 1;
      });
      if (writes) await batch.commit();
    };
    queue().catch(error => console.error('S7 staff reminder queue failed', error));
  }, [orgId, responseCentreItems, staffDirectory, responseSettings]);

  useEffect(() => {
    if (!orgId) return undefined;
    const allowed = Array.from(new Set(siteScope.siteIds || [])).filter(Boolean);
    if (!siteScope.allSites && !allowed.length) {
      setResponseNotifications([]);
      return undefined;
    }
    const ref = collection(db, 'orgs', orgId, 'neoNotifications');
    const scoped = siteScope.allSites ? ref : query(ref, where('storeId', 'in', allowed.slice(0, 30)));
    const unsub = onSnapshot(scoped, snap => {
      const rows = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }))
        .sort((a, b) => (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0));
      setResponseNotifications(rows);
    }, err => {
      console.error('Could not load Neo notification log', err);
      setResponseNotifications([]);
    });
    return unsub;
  }, [orgId, siteScope.allSites, siteScope.siteIds]);

  useEffect(() => {
    if (!orgId) return undefined;
    const refAll = doc(db, 'orgs', orgId, 'dailyMessages', 'all');
    const refStore = storeId ? doc(db, 'orgs', orgId, 'dailyMessages', `store_${storeId}`) : null;
    let allText = '';
    let storeText = '';
    const pick = () => {
      if (storeText) {
        setDailyMessage(storeText);
        setMessageApplyAll(false);
        return;
      }
      setDailyMessage(allText);
      setMessageApplyAll(true);
    };
    const unsubs = [onSnapshot(refAll, snap => { allText = snap.exists() ? ((snap.data() || {}).text || '').trim() : ''; pick(); })];
    if (refStore) unsubs.push(onSnapshot(refStore, snap => { storeText = snap.exists() ? ((snap.data() || {}).text || '').trim() : ''; pick(); }));
    return () => unsubs.forEach(u => u && u());
  }, [orgId, storeId, siteScope.allSites, siteScope.siteIds]);

  useEffect(() => {
    if (!orgId || !storeId) return undefined;
    const scopedSections = query(
      collection(db, 'orgs', orgId, 'staff_dashboard'),
      where('storeId', '==', storeId)
    );
    const unsub = onSnapshot(scopedSections, snap => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...(d.data() || {}) }))
        .filter(s => !(s?.isArchived === true || s?.archived === true))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSections(list);
    });
    return unsub;
  }, [orgId, storeId, siteScope.allSites, siteScope.siteIds]);

  const selectedStore = stores.find(s => s.id === storeId);
  const selectedActiveStaffNames = useMemo(() => activeStaffNamesForStore(activeStaffRows, selectedStore), [activeStaffRows, selectedStore]);
  const activeStaffByStore = useMemo(() => {
    const map = {};
    stores.forEach(s => { map[s.id] = activeStaffNamesForStore(activeStaffRows, s); });
    return map;
  }, [activeStaffRows, stores]);
  const allFields = useMemo(() => sections.flatMap(sec => (sec.fields || []).map(f => ({ ...f, sectionName: sec.name, sectionId: sec.id }))), [sections]);
  // Alarm summaries must reflect duties that are actually live. Disabled/inactive
  // duties keep their responseRule configuration so it can be restored later,
  // but that saved configuration must not count as an active alarm.
  const activeFields = useMemo(
    () => allFields.filter(f => (f.label || '').trim() && !f?.disabled && !f?.inactive),
    [allFields]
  );
  const responseFields = useMemo(() => activeFields.filter(f => normalizeResponseRule(f?.responseRule).active), [activeFields]);
  const unconfiguredFields = useMemo(() => activeFields.filter(f => !normalizeResponseRule(f?.responseRule).active), [activeFields]);
  const alarmCoverage = activeFields.length ? Math.round((responseFields.length / activeFields.length) * 100) : 0;
  const riskRows = useMemo(() => {
    const rank = { Escalated: 0, Delayed: 1, 'Due Soon': 2, Scheduled: 3 };
    return responseFields.map((f, index) => {
      const risk = riskStatusForRule(f.responseRule) || { status: 'Scheduled', tone: 'green', timeText: f.responseRule?.dueTime || '—' };
      return { ...f, risk, rowId: `${f.sectionId}-${index}` };
    }).sort((a, b) => (rank[a.risk.status] ?? 9) - (rank[b.risk.status] ?? 9) || String(a.responseRule?.dueTime || '').localeCompare(String(b.responseRule?.dueTime || '')));
  }, [responseFields]);
  const dueSoon = riskRows.filter(r => ['Due Soon', 'Due Now'].includes(r.risk.status)).length;
  const delayed = riskRows.filter(r => r.risk.status === 'Delayed').length;
  const escalated = riskRows.filter(r => r.risk.status === 'Escalated').length;
  const compliance = responseFields.length ? Math.max(0, Math.round(((responseFields.length - delayed - escalated) / responseFields.length) * 100)) : 100;

  const visibleResponseCentreItems = useMemo(() => {
    return responseCentreItems
      .filter(item => !storeId || item.storeId === storeId)
      .filter(item => item.active !== false && item.archived !== true && item.deleted !== true)
      .map(item => {
        const computedStatus = queueItemStatus(item);
        const enriched = { ...item, computedStatus };
        return { ...enriched, managerIntel: managerResponseIntel(enriched) };
      });
  }, [responseCentreItems, storeId]);

  const responseCentreGroups = useMemo(() => ({
    due: visibleResponseCentreItems.filter(i => i.computedStatus === 'due'),
    overdue: visibleResponseCentreItems.filter(i => i.computedStatus === 'overdue'),
    escalated: visibleResponseCentreItems.filter(i => i.computedStatus === 'escalated'),
    acknowledged: visibleResponseCentreItems.filter(i => i.computedStatus === 'acknowledged'),
    resolved: visibleResponseCentreItems.filter(i => i.computedStatus === 'resolved'),
  }), [visibleResponseCentreItems]);

  // S7.11 — Duty Intelligence Dashboard.
  // These are operational states, not generic alert buckets.
  const dutyIntelligenceGroups = useMemo(() => {
    const unresolved = visibleResponseCentreItems.filter(i => i.computedStatus !== 'resolved');

    const missingOwner = unresolved.filter(i =>
      ['owner_unavailable', 'awaiting_replacement'].includes(String(i.lifecycleStatus || '')) ||
      ['missing_owner', 'coverage_gap', 'attendance_gap'].includes(String(i.managerAttentionType || ''))
    );
    const missingIds = new Set(missingOwner.map(i => i.id));

    const escalated = unresolved.filter(i =>
      !missingIds.has(i.id) &&
      (String(i.lifecycleStatus || '') === 'escalated' || i.computedStatus === 'escalated')
    );
    const escalatedIds = new Set(escalated.map(i => i.id));

    const overdue = unresolved.filter(i =>
      !missingIds.has(i.id) &&
      !escalatedIds.has(i.id) &&
      (String(i.lifecycleStatus || '') === 'overdue' || i.computedStatus === 'overdue')
    );
    const overdueIds = new Set(overdue.map(i => i.id));

    const dueNow = unresolved.filter(i =>
      !missingIds.has(i.id) &&
      !escalatedIds.has(i.id) &&
      !overdueIds.has(i.id) &&
      ['due', 'reminder_sent', 'reassigned'].includes(String(i.lifecycleStatus || ''))
    );
    const dueIds = new Set(dueNow.map(i => i.id));

    const upcoming = unresolved.filter(i =>
      !missingIds.has(i.id) &&
      !escalatedIds.has(i.id) &&
      !overdueIds.has(i.id) &&
      !dueIds.has(i.id) &&
      (
        String(i.lifecycleStatus || '') === 'upcoming' ||
        String(i.executionAssuranceStatus || '') === 'healthy'
      )
    );

    const byDue = (a, b) => (toMillis(a.dueAt) || 0) - (toMillis(b.dueAt) || 0);
    return {
      dueNow: dueNow.sort(byDue),
      upcoming: upcoming.sort(byDue),
      overdue: overdue.sort(byDue),
      missingOwner: missingOwner.sort(byDue),
      escalated: escalated.sort(byDue),
    };
  }, [visibleResponseCentreItems]);

  const visibleResponseNotifications = useMemo(() => {
    return responseNotifications.filter(n => !storeId || n.storeId === storeId).slice(0, 80);
  }, [responseNotifications, storeId]);

  const managerInboxGroups = useMemo(() => ({
    newAlerts: visibleResponseCentreItems
      .filter(i => i.managerIntel?.needsManager === true)
      .filter(i => !i.managerAcknowledged && i.computedStatus !== 'resolved')
      .sort((a, b) => {
        const rank = { red: 0, orange: 1, blue: 2, green: 3 };
        return (rank[a.managerIntel?.severity] ?? 9) - (rank[b.managerIntel?.severity] ?? 9)
          || (toMillis(a.dueAt) || 0) - (toMillis(b.dueAt) || 0);
      }),
    acknowledged: responseCentreGroups.acknowledged,
    resolved: responseCentreGroups.resolved.slice(0, 8),
  }), [visibleResponseCentreItems, responseCentreGroups]);

  const notificationLogRows = useMemo(() => {
    const loggedIds = new Set(visibleResponseNotifications.map(n => n.queueId).filter(Boolean));
    const liveDerived = visibleResponseCentreItems
      .filter(i => i.managerIntel?.needsManager === true)
      .filter(i => i.managerNotificationAllowed !== false)
      .filter(i => !loggedIds.has(i.id))
      .filter((item, index, rows) => {
        const key = item.managerNotificationKey || `${item.id}|${item.managerAttentionType || item.lifecycleStatus || 'attention'}`;
        return rows.findIndex(row => (
          row.managerNotificationKey ||
          `${row.id}|${row.managerAttentionType || row.lifecycleStatus || 'attention'}`
        ) === key) === index;
      })
      .map(i => ({
        id: `live-${i.id}`,
        queueId: i.id,
        dutyLabel: i.dutyLabel || i.label || i.dutyName || 'Duty',
        storeName: i.storeName || i.siteName || 'Selected site',
        status: i.managerAcknowledged ? 'acknowledged' : 'new',
        deliveryMethod: i.escalationMethod || i.escalationLevel1Method || 'dashboard',
        targetRole: i.escalationRole || i.escalationLevel1Role || 'manager',
        createdAt: i.escalatedAt || i.updatedAt || i.dueAt,
        source: 'live_queue',
      }));
    return [...liveDerived, ...visibleResponseNotifications]
      .sort((a, b) => (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0))
      .slice(0, 20);
  }, [visibleResponseNotifications, visibleResponseCentreItems]);

  const executionAssurance = useMemo(() => {
    const unresolved = visibleResponseCentreItems.filter(i => i.computedStatus !== 'resolved');
    const attention = unresolved.filter(i => i.managerIntel?.needsManager === true);
    const handled = unresolved.filter(i =>
      i.managerIntel?.needsManager !== true &&
      (i.executionAssuranceStatus === 'handled_by_neo' ||
       ['due', 'reminder_sent', 'reassigned'].includes(String(i.lifecycleStatus || '')))
    );
    const healthy = unresolved.filter(i =>
      i.managerIntel?.needsManager !== true &&
      !handled.includes(i)
    );

    return {
      allClear: attention.length === 0,
      attention: attention.length,
      handled: handled.length,
      healthy: healthy.length,
      total: unresolved.length,
      dueNow: dutyIntelligenceGroups.dueNow.length,
      upcoming: dutyIntelligenceGroups.upcoming.length,
      overdue: dutyIntelligenceGroups.overdue.length,
      missingOwner: dutyIntelligenceGroups.missingOwner.length,
      escalated: dutyIntelligenceGroups.escalated.length,
    };
  }, [visibleResponseCentreItems, dutyIntelligenceGroups]);

  const s7ScenarioAssurance = useMemo(() => {
    const unresolved = visibleResponseCentreItems.filter(i => i.computedStatus !== 'resolved');

    const counts = {
      healthy: 0,
      staffResponse: 0,
      managerAttention: 0,
      reassigned: 0,
      attendanceGap: 0,
      missingOwner: 0,
      overdue: 0,
      escalated: 0,
    };

    unresolved.forEach(item => {
      const discipline = String(item.notificationDisciplineState || '');
      const lifecycle = String(item.lifecycleStatus || '');
      const attendance = String(item.attendanceStatus || '');
      const attention = String(item.managerAttentionType || '');

      if (discipline === 'healthy_silent' || discipline === 'silent') counts.healthy += 1;
      if (discipline === 'staff_response' || discipline === 'neo_handling') counts.staffResponse += 1;
      if (discipline === 'manager_attention') counts.managerAttention += 1;
      if (lifecycle === 'reassigned' || attendance === 'covered_by_replacement') counts.reassigned += 1;
      if (['attendance_gap', 'coverage_gap'].includes(attention)) counts.attendanceGap += 1;
      if (attention === 'missing_owner' || ['owner_unavailable', 'awaiting_replacement'].includes(lifecycle)) counts.missingOwner += 1;
      if (lifecycle === 'overdue' || attention === 'overdue') counts.overdue += 1;
      if (lifecycle === 'escalated' || attention === 'escalated') counts.escalated += 1;
    });

    return counts;
  }, [visibleResponseCentreItems]);

  const responseCentreKpis = useMemo(() => ({
    attention: executionAssurance.attention,
    active: responseCentreGroups.due.length + responseCentreGroups.overdue.length + responseCentreGroups.escalated.length + responseCentreGroups.acknowledged.length,
    overdue: responseCentreGroups.overdue.length,
    escalated: responseCentreGroups.escalated.length,
    acknowledged: responseCentreGroups.acknowledged.length,
    resolved: responseCentreGroups.resolved.length,
  }), [visibleResponseCentreItems, responseCentreGroups]);

  async function updateQueueItem(item, patch, successMsg) {
    if (!orgId || !item?.id) return;
    try {
      const touchedBy = actor?.name || actor?.displayName || actor?.email || 'Manager';
      const touchedByUid = actor?.uid || '';
      const ownershipPatch = patch.managerStatus === 'acknowledged'
        ? {
            managerAcknowledged: true,
            managerAcknowledgedAt: serverTimestamp(),
            assignedTo: touchedBy,
            assignedToUid: touchedByUid,
            assignedAt: serverTimestamp(),
            ownerName: touchedBy,
            ownerUid: touchedByUid,
            responseOwner: touchedBy,
            responseOwnerUid: touchedByUid,
            responseOwnerStatus: 'owned',
          }
        : {};
      const finalPatch = { ...patch, ...ownershipPatch };
      await updateDoc(doc(db, 'orgs', orgId, 'neoDutyQueue', item.id), {
        ...finalPatch,
        managerTouchedAt: serverTimestamp(),
        managerTouchedBy: touchedBy,
        managerTouchedByUid: touchedByUid,
      });
      const eventType = finalPatch.managerStatus === 'acknowledged'
        ? 'acknowledged'
        : finalPatch.managerStatus === 'escalated_now' || finalPatch.escalationStatus === 'ready'
          ? 'escalated'
          : 'updated';
      await addDoc(collection(db, 'orgs', orgId, 'neoNotifications'), {
        orgId,
        queueId: item.id,
        standardId: item.standardId || '',
        standardName: item.standardName || '',
        dutyId: item.dutyId || '',
        dutyLabel: item.dutyLabel || item.label || item.dutyName || 'Duty',
        storeId: item.storeId || storeId || '',
        storeName: item.storeName || item.siteName || selectedStore?.name || '',
        status: eventType,
        managerStatus: finalPatch.managerStatus || item.managerStatus || '',
        escalationStatus: finalPatch.escalationStatus || item.escalationStatus || '',
        escalationLevel: item.escalationLevel || 'Level 1',
        deliveryMethod: item.escalationMethod || item.escalationLevel1Method || 'dashboard',
        targetRole: item.escalationRole || item.escalationLevel1Role || 'manager',
        target: item.escalateTo || item.escalationTarget || '',
        assignedTo: finalPatch.assignedTo || item.assignedTo || item.ownerName || '',
        assignedToUid: finalPatch.assignedToUid || item.assignedToUid || item.ownerUid || '',
        dueAt: item.dueAt || null,
        dueTime: item.dueTime || '',
        createdAt: serverTimestamp(),
        createdBy: touchedBy,
        createdByUid: touchedByUid,
        read: eventType === 'acknowledged',
      });
      if (eventType === 'acknowledged') {
        await addDoc(collection(db, 'orgs', orgId, 'neoTimeline'), {
          orgId,
          queueId: item.id,
          standardId: item.standardId || '',
          standardName: item.standardName || '',
          dutyId: item.dutyId || '',
          dutyLabel: item.dutyLabel || item.label || item.dutyName || 'Duty',
          storeId: item.storeId || storeId || '',
          storeName: item.storeName || item.siteName || selectedStore?.name || '',
          title: 'Escalation acknowledged',
          type: 'manager_acknowledged',
          managerStatus: 'acknowledged',
          assignedTo: touchedBy,
          assignedToUid: touchedByUid,
          createdAt: serverTimestamp(),
          actorName: touchedBy,
          actorId: touchedByUid,
        });
      }
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      console.error(e);
      toast.error('Could not update response item');
    }
  }

  const canAdd = useMemo(() => {
    const n = newSec;
    return !!orgId && (applyAllStores ? stores.length > 0 : !!storeId) && n.name.trim() && (n.subheading.trim() || (n.fieldLabel.trim() && n.fieldType));
  }, [newSec, orgId, stores.length, storeId, applyAllStores]);

  const buildFields = ({ subheading, fieldLabel, fieldType }) => {
    const fields = [];
    if (subheading.trim() && !fieldLabel.trim()) fields.push({ subheading: subheading.trim(), label: '', type: '' });
    else if (!subheading.trim() && fieldLabel.trim()) fields.push({ subheading: '', label: fieldLabel.trim(), type: fieldType });
    else if (subheading.trim() && fieldLabel.trim()) fields.push({ subheading: subheading.trim(), label: fieldLabel.trim(), type: fieldType });
    return fields;
  };

  const cardStyle = { backgroundColor: 'var(--brand-surface)', borderColor: 'rgba(255,255,255,0.08)' };
  const headerStyle = { backgroundColor: 'rgba(255,255,255,0.05)' };
  const accentBtn = { backgroundColor: 'var(--brand-accent)', color: 'white' };
  const thStyle = { background: 'rgba(255,255,255,0.06)', padding: '10px 12px', fontWeight: 700 };
  const tdStyle = { padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.06)' };
  const kpiCard = 'rounded-xl border p-4 min-h-[110px] flex flex-col justify-between';

  const pushWarning = (msg) => { setBanner(msg); setTimeout(() => setBanner(''), 3500); };
  const toggleExpand = id => setExpanded(e => ({ ...e, [id]: !e[id] }));

  async function saveDailyMessage() {
    if (!orgId) return;
    try {
      if (messageApplyAll) {
        await setDoc(doc(db, 'orgs', orgId, 'dailyMessages', 'all'), {
          orgId,
          scope: 'all',
          storeId: null,
          text: dailyMessage || '',
          updatedAt: serverTimestamp(),
          updatedBy: actor?.name || '',
          updatedByUid: actor?.uid || '',
          updatedByEmail: actor?.email || '',
        }, { merge: true });
        if (storeId) {
          try { await deleteDoc(doc(db, 'orgs', orgId, 'dailyMessages', `store_${storeId}`)); } catch (e) {}
        }
      } else if (storeId) {
        await setDoc(doc(db, 'orgs', orgId, 'dailyMessages', `store_${storeId}`), {
          orgId,
          scope: 'store',
          storeId,
          text: dailyMessage || '',
          updatedAt: serverTimestamp(),
          updatedBy: actor?.name || '',
          updatedByUid: actor?.uid || '',
          updatedByEmail: actor?.email || '',
        }, { merge: true });
      }
      setMsgSaved(true);
      setTimeout(() => setMsgSaved(false), 1800);
    } catch (e) {
      console.error(e);
      toast.error('Failed to save daily message');
    }
  }

  const handleAddSection = async () => {
    if (!canAdd) return;
    const fields = buildFields(newSec);
    const payload = sid => ({
      orgId,
      storeId: sid,
      name: newSec.name.trim(),
      order: Date.now(),
      fields,
      isFixedCashup: false,
      createdAt: serverTimestamp(),
      createdBy: actor?.name || '',
      createdByUid: actor?.uid || '',
      createdByEmail: actor?.email || '',
      updatedAt: serverTimestamp(),
      updatedBy: actor?.name || '',
      updatedByUid: actor?.uid || '',
      updatedByEmail: actor?.email || '',
    });
    try {
      if (applyAllStores) await Promise.all(stores.map(s => addDoc(collection(db, 'orgs', orgId, 'staff_dashboard'), payload(s.id))));
      else await addDoc(collection(db, 'orgs', orgId, 'staff_dashboard'), payload(storeId));
      setNewSec({ name: '', subheading: '', fieldLabel: '', fieldType: '' });
    } catch (e) {
      console.error(e);
      toast.error('Could not create section');
    }
  };

  const moveSection = async (idx, dir) => {
    const target = dir === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= sections.length) return;
    const A = sections[idx];
    const B = sections[target];
    await Promise.all([
      updateDoc(doc(db, 'orgs', orgId, 'staff_dashboard', A.id), { order: B.order, updatedAt: serverTimestamp() }),
      updateDoc(doc(db, 'orgs', orgId, 'staff_dashboard', B.id), { order: A.order, updatedAt: serverTimestamp() }),
    ]);
  };

  const sectionHasSubmissions = async (sectionName) => {
    if (!orgId || !sectionName) return false;
    try {
      const legacyQ = query(collection(db, 'orgs', orgId, 'submissions'), where('sectionName', '==', sectionName), limit(1));
      const legacySnap = await getDocs(legacyQ);
      if (!legacySnap.empty) return true;
    } catch (e) {}
    for (const st of stores) {
      try {
        const qy = query(collection(db, 'orgs', orgId, 'stores', st.id, 'submissions'), where('sectionName', '==', sectionName), limit(1));
        const snap = await getDocs(qy);
        if (!snap.empty) return true;
      } catch (e) {}
    }
    return false;
  };

  const handleDelete = async (row) => {
    if (!row) return;
    setConfirmDel(null);
    const hasSubs = await sectionHasSubmissions(row.name);
    const archivePayload = { isArchived: true, archived: true, archivedAt: serverTimestamp(), archivedBy: actor?.name || '', updatedAt: serverTimestamp() };
    if (applyAllStores) {
      const snap = await getDocs(query(collection(db, 'orgs', orgId, 'staff_dashboard'), where('name', '==', row.name)));
      const batch = writeBatch(db);
      snap.docs.forEach(d => {
        const ref = doc(db, 'orgs', orgId, 'staff_dashboard', d.id);
        if (hasSubs) batch.update(ref, archivePayload);
        else batch.delete(ref);
      });
      await batch.commit();
      return;
    }
    const ref = doc(db, 'orgs', orgId, 'staff_dashboard', row.id);
    if (hasSubs) await updateDoc(ref, archivePayload);
    else await deleteDoc(ref);
  };

  const startEdit = (sec) => {
    setActiveTab('builder');
    setBanner('');
    const groups = {};
    (sec.fields || []).forEach(({ subheading, label, type, core, responseRule, instructions, disabled, inactive, dutyId, ownerRoleId, ownerRoleName, responsibilityRouting, priority, recurrence, completionSource, morpheus, morpheusLinkId }) => {
      const key = (subheading || '').trim();
      groups[key] = groups[key] || { subheading: key, fields: [] };
      groups[key].fields.push({ ...(completionSource==='morpheus' ? {completionSource,morpheus,morpheusLinkId:morpheusLinkId || ''} : {}), label: label || '', type: type || '', instructions: instructions || '', core: !!core, disabled: !!(disabled || inactive), dutyId: dutyId || '', ownerRoleId: ownerRoleId || '', ownerRoleName: ownerRoleName || '', responsibilityRouting: responsibilityRouting || { mode: 'manager_decision', fallbackRoles: [] }, priority: priority || 'normal', recurrence: recurrence || { mode: 'daily', days: [] }, responseRule: normalizeResponseRule(responseRule) });
    });
    const draftStorageKey = `neo:staffDashboard:draft:${orgId}:${sec.id}`;
    const baseEdit = {
      id: sec.id,
      expectedStandardVersion: sec.publishedStandardVersion ?? null,
      hadMorpheusDuties: (sec.fields || []).some(f=>f.completionSource==='morpheus'),
      storeId,
      storeName: selectedStore?.name || sec.storeName || 'Selected store',
      draftName: sec.name,
      description: sec.description || '',
      status: sec.status || 'active',
      responseDefaults: { ...(sec.responseDefaults || { escalateTo: '', escalationMethod: 'email', reminderBeforeMins: '10', gracePeriodMins: '10', repeatEveryMins: '10', quietHoursStart: '', quietHoursEnd: '' }), escalationMethod: normalizeEscalationMethodValue(sec.responseDefaults?.escalationMethod || 'email') },
      sectionResponseTarget: normalizeAggregateResponseTarget(sec.sectionResponseTarget),
      subheadingResponseTargets: Object.fromEntries(
        Object.entries(sec.subheadingResponseTargets || {}).map(([key, value]) => [key, normalizeAggregateResponseTarget(value)])
      ),
      draftFields: Object.values(groups),
      newSub: '',
      newField: { subheading: '', label: '', type: '' },
      order: sec.order ?? Date.now(),
      applyAll: storeId === 'all' || selectedStore?.name === 'All Sites',
      draftStorageKey,
      hasLocalDraft: false,
    };
    setEditing(baseEdit);
    getDoc(doc(db,'orgs',orgId,'neoStandards',sec.id)).then(snap=>setEditing(cur=>cur?.id===sec.id ? {...cur,expectedStandardVersion:snap.exists()?Number(snap.data().version || 0):0,hadMorpheusDuties:cur.hadMorpheusDuties || (snap.data()?.duties || []).some(d=>d.completionSource==='morpheus')} : cur)).catch(()=>{});
    draftDbGet(draftStorageKey)
      .then((savedDraft) => {
        if (!savedDraft) return;
        setEditing(current => {
          if (!current || current.id !== sec.id || current.draftStorageKey !== draftStorageKey) return current;
          return { ...baseEdit, ...savedDraft, expectedStandardVersion:current.expectedStandardVersion, hadMorpheusDuties:current.hadMorpheusDuties, id: sec.id, draftStorageKey, storeId, storeName: baseEdit.storeName, hasLocalDraft: true };
        });
      })
      .catch((e) => console.warn('Unable to load staff dashboard draft', e));
  };

  const updateDraftField = (gi, fi, patch) => setEditing(ed => ({
    ...ed,
    draftFields: ed.draftFields.map((g, i) => (i === gi ? { ...g, fields: g.fields.map((f, j) => (j === fi ? { ...f, ...patch } : f)) } : g)),
  }));

  const updateResponseRule = (gi, fi, patch) => {
    setEditing(ed => ({
      ...ed,
      draftFields: ed.draftFields.map((g, i) => (i === gi ? {
        ...g,
        fields: g.fields.map((f, j) => (j === fi ? { ...f, responseRule: { ...normalizeResponseRule(f.responseRule), ...patch } } : f)),
      } : g)),
    }));
  };

  const handleSaveEdit = async (options = {}) => {
    const closeAfterSave = options.close !== false;
    if (!editing) return;
    const {
      id, draftName, description, status, responseDefaults = {}, draftFields, applyAll, order,
      sectionResponseTarget = {}, subheadingResponseTargets = {},
    } = editing;
    const flat = [];
    for (const group of draftFields) {
      const sub = (group.subheading || '').trim();
      for (const f of (group.fields || [])) {
        if (!f.label?.trim() && !f.type) continue;
        const rule = cleanPublishedRule(f.responseRule);
        const alarmIsActive = !!rule.active;
        flat.push({
          subheading: sub,
          label: f.label.trim(),
          type: f.type || '',
          hasAlarm: alarmIsActive,
          ...(f.instructions ? { instructions: f.instructions } : {}),
          ...(f.dutyId ? { dutyId: f.dutyId } : {}),
          ...(f.completionSource==='morpheus' ? {completionSource:'morpheus',morpheus:f.morpheus,...(f.morpheusLinkId?{morpheusLinkId:f.morpheusLinkId}:{})} : {}),
          ...(f.ownerRoleId ? { ownerRoleId: f.ownerRoleId, ownerRoleName: f.ownerRoleName || '' } : {}),
          responsibilityRouting: f.responsibilityRouting || { mode: 'manager_decision', fallbackRoles: [] },
          priority: f.priority || 'normal',
          recurrence: f.recurrence || { mode: 'daily', days: [] },
          ...(f.core ? { core: true } : {}),
          ...(f.disabled ? { disabled: true } : {}),
          // Always keep responseRule when it has settings, even if the alarm is switched off.
          // This preserves due/reminder/escalation values for the next time the alarm is turned on.
          ...(responseRuleHasSettings(rule) || alarmIsActive ? { responseRule: rule } : {}),
        });
      }
    }
    try {
      const usesMorpheus = editing.hadMorpheusDuties || flat.some(f=>f.completionSource==='morpheus');
      if(usesMorpheus && (applyAll || storeId==='all')) throw new Error('Morpheus duties need a single Neo site. Turn off Include all stores and map each site separately.');
      // Deactivation is not deletion.
      // Keep the complete duty definition in staff_dashboard so an inactive duty
      // can be edited/reactivated later. Only active duties are projected into
      // neoStandards, which is the live Neo App source of truth.
      const publishedFields = flat.filter(f => (f.label || '').trim() && !f.disabled);
      const fieldsToPersist = flat;

      const sectionTargetToPersist = normalizeAggregateResponseTarget(sectionResponseTarget);
      const subheadingTargetsToPersist = Object.fromEntries(
        Object.entries(subheadingResponseTargets || {}).map(([key, value]) => [key, normalizeAggregateResponseTarget(value)])
      );

      if(!options.publish || !usesMorpheus) await updateDoc(doc(db, 'orgs', orgId, 'staff_dashboard', id), {
        name: draftName.trim(),
        description: (description || '').trim(),
        status: status || 'active',
        responseDefaults,
        sectionResponseTarget: sectionTargetToPersist,
        subheadingResponseTargets: subheadingTargetsToPersist,
        fields: fieldsToPersist,
        publishState: options.publish ? 'published' : 'draft',
        ...(options.publish ? { publishedAt: serverTimestamp() } : { draftSavedAt: serverTimestamp() }),
        updatedAt: serverTimestamp(),
      });

      if (options.publish) {
        const activeFields = publishedFields;
        const duties = activeFields.map((f, index) => {
          const rule = cleanPublishedRule(f.responseRule);
          const dutyId = f.dutyId || `${slugifyDutyId(f.label)}-${index}`;
          return {
            dutyId,
            ...(f.completionSource==='morpheus'?{completionSource:'morpheus',morpheus:f.morpheus,...(f.morpheusLinkId?{morpheusLinkId:f.morpheusLinkId}:{})}:{}),
            staffDashboardSectionId: id,
            subheading: f.subheading || 'General',
            label: f.label || '',
            type: f.type || 'checkbox',
            instructions: f.instructions || '',
            ownerRoleId: f.ownerRoleId || '',
            ownerRoleName: f.ownerRoleName || '',
            responsibilityRouting: f.responsibilityRouting || { mode: 'manager_decision', fallbackRoles: [] },
            ownershipMode: f.ownerRoleId ? 'scheduled_role' : 'any_scheduled_staff',
            priority: f.priority || 'normal',
            recurrence: f.recurrence || { mode: 'daily', days: [] },
            order: index,
            active: !f.disabled,
            disabled: !!f.disabled,
            core: !!f.core,
            hasAlarm: !!rule.active,
            responseRule: responseRuleHasSettings(rule) || rule.active ? rule : emptyResponseRule(),
          };
        });
        const activeSubheadings = new Set(
          duties.filter(d => d.active && !d.disabled).map(d => String(d.subheading || 'General').trim())
        );
        const responseTargets = [];

        const sectionTarget = cleanAggregateResponseTarget(sectionTargetToPersist, responseDefaults);
        if (sectionTarget.active && sectionTarget.responseRule?.dueTime) {
          responseTargets.push({
            targetId: `aggregate-section-${slugifyDutyId(id)}`,
            targetType: 'section',
            targetKey: id,
            label: draftName.trim(),
            subheading: '',
            completionCondition: 'section_submitted',
            ownerRoleId: sectionTarget.ownerRoleId || '',
            ownerRoleName: sectionTarget.ownerRoleName || '',
            ownershipMode: sectionTarget.ownerRoleId ? 'scheduled_role' : 'any_scheduled_staff',
            responsibilityRouting: { mode: sectionTarget.ownerRoleId ? 'scheduled_role' : 'manager_decision', fallbackRoles: [] },
            childDutyIds: duties.filter(d => d.active && !d.disabled).map(d => d.dutyId),
            childDutyLabels: duties.filter(d => d.active && !d.disabled).map(d => d.label),
            active: true,
            disabled: false,
            responseRule: sectionTarget.responseRule,
          });
        }

        Object.entries(subheadingTargetsToPersist || {}).forEach(([subheading, rawTarget]) => {
          const cleanSubheading = String(subheading || '').trim();
          if (!cleanSubheading || !activeSubheadings.has(cleanSubheading)) return;
          const target = cleanAggregateResponseTarget(rawTarget, responseDefaults);
          if (!target.active || !target.responseRule?.dueTime) return;
          const children = duties.filter(d => d.active && !d.disabled && String(d.subheading || 'General').trim() === cleanSubheading);
          responseTargets.push({
            targetId: `aggregate-subheading-${slugifyDutyId(cleanSubheading)}`,
            targetType: 'subheading',
            targetKey: cleanSubheading,
            label: cleanSubheading,
            subheading: cleanSubheading,
            completionCondition: 'all_fields_complete',
            ownerRoleId: target.ownerRoleId || '',
            ownerRoleName: target.ownerRoleName || '',
            ownershipMode: target.ownerRoleId ? 'scheduled_role' : 'any_scheduled_staff',
            responsibilityRouting: { mode: target.ownerRoleId ? 'scheduled_role' : 'manager_decision', fallbackRoles: [] },
            childDutyIds: children.map(d => d.dutyId),
            childDutyLabels: children.map(d => d.label),
            active: true,
            disabled: false,
            responseRule: target.responseRule,
          });
        });

        const activeDutyCount = duties.filter(d => d.active && !d.disabled).length;
        const activeAlarmCount = duties.filter(d => d.active && !d.disabled && d.responseRule?.active).length;
        const activeResponseTargetCount = responseTargets.length;
        const standardPayload = {
          orgId,
          staffDashboardSectionId: id,
          name: draftName.trim(),
          description: (description || '').trim(),
          status: status || 'active',
          publishState: 'published',
          storeId,
          storeName: selectedStore?.name || editing.storeName || '',
          appliesToAllStores: !!applyAll,
          responseDefaults,
          duties,
          responseTargets,
          activeDutyCount,
          activeAlarmCount,
          activeResponseTargetCount,
          totalResponseAlarmCount: activeAlarmCount + activeResponseTargetCount,
          alarmCoverage: activeDutyCount ? Math.round((activeAlarmCount / activeDutyCount) * 100) : 0,
          timezone: 'Africa/Johannesburg',
          version: Date.now(),
          publishedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        if(usesMorpheus) {
          const {publishedAt,updatedAt,...standard}=standardPayload;
          const result=(await publishMorpheusSection({orgId,storeId,action:'publishSection',standardId:id,expectedVersion:editing.expectedStandardVersion,standard,dashboard:{name:draftName.trim(),description:(description || '').trim(),status:status || 'active',responseDefaults,sectionResponseTarget:sectionTargetToPersist,subheadingResponseTargets:subheadingTargetsToPersist,fields:fieldsToPersist}})).data;
          setEditing(cur=>cur?.id===id?{...cur,expectedStandardVersion:result.version,hadMorpheusDuties:flat.some(f=>f.completionSource==='morpheus'),draftFields:cur.draftFields.map(g=>({...g,fields:g.fields.map(f=>{const binding=(result.bindings || []).find(b=>b.dutyId===f.dutyId);return binding?{...f,morpheusLinkId:binding.linkId}:f;})}))}:cur);
        } else await setDoc(doc(db,'orgs',orgId,'neoStandards',id),standardPayload,{merge:true});
      }
      if (applyAll) {
        const all = await getDocs(collection(db, 'orgs', orgId, 'staff_dashboard'));
        const me = all.docs.find(d => d.id === id);
        const sourceName = (me?.data()?.name || '').trim();
        await Promise.all(stores.map(async s => {
          const mine = sections.find(sec => sec.id === id);
          if (mine && mine.storeId === s.id) return;
          const match = all.docs.find(d => (d.data() || {}).storeId === s.id && String((d.data() || {}).name || '').trim() === sourceName);
          const payload = {
            name: draftName.trim(),
            description: (description || '').trim(),
            status: status || 'active',
            responseDefaults,
            sectionResponseTarget: sectionTargetToPersist,
            subheadingResponseTargets: subheadingTargetsToPersist,
            fields: flat,
            publishState: options.publish ? 'published' : 'draft',
            ...(options.publish ? { publishedAt: serverTimestamp() } : { draftSavedAt: serverTimestamp() }),
            updatedAt: serverTimestamp(),
          };
          if (match) await updateDoc(doc(db, 'orgs', orgId, 'staff_dashboard', match.id), payload);
          else await addDoc(collection(db, 'orgs', orgId, 'staff_dashboard'), { orgId, storeId: s.id, order, ...payload, createdAt: serverTimestamp() });
        }));
      }
      try { await logAudit(db, actor, { action: 'UPDATE_SECTION', page: 'Staff Dashboard', entityType: 'section', entityId: id, storeId, meta: { responseRules: flat.filter(f => f.responseRule).length } }); } catch (e) {}
      try {
        if (options.publish && editing?.draftStorageKey) await draftDbDelete(editing.draftStorageKey);
      } catch (e) {}
      if (closeAfterSave) setEditing(null);
      return true;
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Could not save section');
      return false;
    }
  };

  function groupFields(fields = []) {
    const groups = {};
    fields.forEach(f => {
      const key = (f.subheading || '').trim();
      (groups[key] ||= []).push(f);
    });
    return Object.entries(groups).map(([sub, items]) => ({ sub, items }));
  }

  const dedupeCurrentStore = async () => {
    if (!orgId || !storeId) return;
    const snap = await getDocs(query(collection(db, 'orgs', orgId, 'staff_dashboard'), where('storeId', '==', storeId)));
    const rows = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
    const byName = new Map();
    rows.forEach(r => {
      const k = String(r.name || '').trim();
      if (!k) return;
      if (!byName.has(k)) byName.set(k, []);
      byName.get(k).push(r);
    });
    const dups = [];
    for (const arr of byName.values()) {
      if (arr.length > 1) {
        arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        dups.push(...arr.slice(1));
      }
    }
    if (!dups.length) {
      pushWarning('No duplicates found for this store.');
      return;
    }
    const batch = writeBatch(db);
    for (const row of dups) {
      const hasSubs = await sectionHasSubmissions(row.name);
      const ref = doc(db, 'orgs', orgId, 'staff_dashboard', row.id);
      if (hasSubs) batch.update(ref, { isArchived: true, archived: true, archivedAt: serverTimestamp(), updatedAt: serverTimestamp() });
      else batch.delete(ref);
    }
    await batch.commit();
    pushWarning(`Removed ${dups.length} duplicate section(s) for this store.`);
  };

  const saveSettings = async () => {
    try {
      await setDoc(doc(db, 'orgs', orgId, 'neo_settings', 'staffDashboardResponse'), { ...responseSettings, updatedAt: serverTimestamp(), updatedBy: actor?.name || '' }, { merge: true });
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 1800);
    } catch (e) {
      console.error(e);
      toast.error('Could not save response settings');
    }
  };

  if (!orgId) return <div className="p-6 text-white/70">Loading…</div>;

  const renderStorePicker = () => (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-3">
        <label className="opacity-80">Store</label>
        <select className="px-3 py-2 rounded border border-white/10" style={{ backgroundColor: 'var(--brand-surface)', color: 'var(--brand-text)' }} value={storeId} onChange={e => setStoreId(e.target.value)}>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="text-sm opacity-70">Viewing: <span className="font-semibold text-white">{selectedStore?.name || 'No store selected'}</span></div>
    </div>
  );

  const renderOverview = () => {
    const mostDelayed = riskRows.filter(r => r.risk.status === 'Delayed' || r.risk.status === 'Escalated').slice(0, 5);
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {renderStorePicker()}
          <div className="flex flex-wrap gap-2 text-sm">
            {[['today','Today'],['yesterday','Yesterday'],['7d','7 Days']].map(([key,label]) => (
              <button key={key} onClick={() => setOverviewRange(key)} className={`px-3 py-2 rounded border border-white/10 ${overviewRange === key ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10'}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className={kpiCard} style={cardStyle}><div className="text-sm opacity-70">On Time</div><div className="text-3xl font-bold">{compliance}%</div><div className="text-xs opacity-60">Based on configured alarms</div></div>
          <div className={kpiCard} style={cardStyle}><div className="text-sm opacity-70">Due Soon</div><div className="text-3xl font-bold">{dueSoon}</div><div className="text-xs opacity-60">Within reminder window</div></div>
          <div className={kpiCard} style={cardStyle}><div className="text-sm opacity-70">Delayed</div><div className="text-3xl font-bold">{delayed}</div><div className="text-xs opacity-60">Past due time</div></div>
          <div className={kpiCard} style={cardStyle}><div className="text-sm opacity-70">Escalated</div><div className="text-3xl font-bold">{escalated}</div><div className="text-xs opacity-60">Operator notification threshold</div></div>
          <div className={kpiCard} style={cardStyle}><div className="text-sm opacity-70">Alarm Coverage</div><div className="text-3xl font-bold">{alarmCoverage}%</div><div className="text-xs opacity-60">{unconfiguredFields.length} duties still need rules</div></div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="rounded-xl border p-4 xl:col-span-2" style={cardStyle}>
            <div className="flex items-center justify-between mb-4"><h2 className="text-xl font-semibold">Execution Risk Feed</h2><span className="text-xs px-2 py-1 rounded bg-white/10">Assurance V5</span></div>
            <div className="overflow-auto">
              <table className="min-w-[850px] w-full text-sm">
                <thead><tr><th style={thStyle} className="text-left rounded-l-md">Status</th><th style={thStyle} className="text-left">Site</th><th style={thStyle} className="text-left">Section</th><th style={thStyle} className="text-left">Duty</th><th style={thStyle} className="text-left">Staff</th><th style={thStyle} className="text-left rounded-r-md">Time</th></tr></thead>
                <tbody>
                  {riskRows.length === 0 && <tr><td style={tdStyle} colSpan={6} className="opacity-70">No response alarms configured yet. Use Builder → Response Setup Queue to activate this feed.</td></tr>}
                  {riskRows.map(r => (
                    <tr key={r.rowId}>
                      <td style={tdStyle}><RiskBadge tone={r.risk.tone} label={r.risk.status} /></td>
                      <td style={tdStyle}>{selectedStore?.name || '—'}</td>
                      <td style={tdStyle}>{r.sectionName}</td>
                      <td style={tdStyle}>{r.label || '—'}</td>
                      <td style={tdStyle}>{selectedActiveStaffNames.length ? selectedActiveStaffNames.join(', ') : 'No active staff'}</td>
                      <td style={tdStyle}>{r.risk.timeText}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border p-4" style={cardStyle}>
            <h2 className="text-xl font-semibold mb-4">Sites & On Duty</h2>
            <div className="space-y-2 max-h-80 overflow-auto pr-1">
              {stores.map(s => (
                <div key={s.id} className="rounded-lg p-3 border border-white/10 flex items-center justify-between" style={{ background: s.id === storeId ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.03)' }}>
                  <div><div className="font-semibold">{s.name}</div><div className="text-xs opacity-60">{(activeStaffByStore[s.id] || []).length ? (activeStaffByStore[s.id] || []).join(', ') : 'No one clocked in'}</div></div>
                  <span className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">{(activeStaffByStore[s.id] || []).length} on duty</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <AccountabilityCard title="Site Compliance" rows={stores.slice(0, 6).map((s, i) => ({ label: s.name, value: `${Math.max(60, compliance - (i * 3))}%` }))} />
          <AccountabilityCard title="Staff On Duty" rows={(selectedActiveStaffNames.length ? selectedActiveStaffNames.map(name => ({ label: name, value: `${compliance}%` })) : [{ label: 'No active staff', value: '0' }])} />
          <AccountabilityCard title="Response Setup Queue" rows={(unconfiguredFields.length ? unconfiguredFields.slice(0, 5) : mostDelayed.length ? mostDelayed : responseFields.slice(0, 3)).map((r, i) => ({ label: r.label || r.sectionName, value: unconfiguredFields.length ? 'Needs alarm' : mostDelayed.length ? r.risk.timeText : `${i + 1} configured` }))} />
        </div>
      </div>
    );
  };

  const renderBuilder = () => (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {renderStorePicker()}
        <label className="flex items-center gap-2 text-sm opacity-90"><input type="checkbox" checked={applyAllStores} onChange={e => setApplyAllStores(e.target.checked)} /> Create / archive in <span className="font-semibold">all stores</span></label>
      </div>



      {!!banner && <div className="p-3 rounded text-sm" style={{ background: 'rgba(255,196,0,.12)', border: '1px solid rgba(255,196,0,.35)' }}>{banner}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <div className="p-4 rounded border space-y-3" style={cardStyle}>
            <div className="flex items-center justify-between"><h2 className="text-xl font-semibold">Daily Message</h2><label className="flex items-center gap-2 text-sm opacity-90"><input type="checkbox" checked={messageApplyAll} onChange={e => setMessageApplyAll(e.target.checked)} />Apply to <span className="font-semibold">all stores</span></label></div>
            <textarea className="w-full p-3 rounded border border-white/10" rows={4} style={inputStyle} placeholder="Write a note to everyone clocking in today…" value={dailyMessage} onChange={e => setDailyMessage(e.target.value)} />
            <div className="flex items-center gap-3"><button onClick={saveDailyMessage} className="px-4 py-2 rounded font-semibold" style={accentBtn}>Save Message</button>{msgSaved && <span className="px-3 py-1 rounded text-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Message saved</span>}</div>
          </div>

          <div className="p-4 rounded space-y-3 border" style={cardStyle}>
            <h2 className="text-xl font-semibold">Create New Standard</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <input className="p-2 rounded border border-white/10" style={inputStyle} placeholder="Section Name" value={newSec.name} onChange={e => setNewSec({ ...newSec, name: e.target.value })} />
              <input className="p-2 rounded border border-white/10" style={inputStyle} placeholder="Subheading" value={newSec.subheading} onChange={e => setNewSec({ ...newSec, subheading: e.target.value })} />
              <input className="p-2 rounded border border-white/10" style={inputStyle} placeholder="Duty / Field Label" value={newSec.fieldLabel} onChange={e => setNewSec({ ...newSec, fieldLabel: e.target.value })} />
              <select className="p-2 rounded border border-white/10" style={inputStyle} value={newSec.fieldType} onChange={e => setNewSec({ ...newSec, fieldType: e.target.value })}><option value="">Type</option>{FIELD_TYPES.map(t => <option key={t} value={t} style={darkSelectOptionStyle}>{t}</option>)}</select>
              <button onClick={handleAddSection} disabled={!canAdd} className="px-4 py-2 rounded font-semibold disabled:opacity-50" style={{ ...accentBtn, opacity: canAdd ? 1 : 0.5 }}>+ Add</button>
            </div>
          </div>

          <div className="rounded-xl border p-4" style={cardStyle}>
            <div className="flex items-center justify-between mb-3"><h2 className="text-xl font-semibold">Standards Library</h2><button onClick={() => setDedupeOpen(true)} className="px-3 py-2 rounded bg-white/10 hover:bg-white/20 text-sm">Remove duplicates</button></div>
            <div className="space-y-3">
              {sections.map((sec, idx) => {
                const groups = groupFields(sec.fields);
                const alarmCount = (sec.fields || []).filter(
                  f => (f?.label || '').trim() && !f?.disabled && !f?.inactive && normalizeResponseRule(f?.responseRule).active
                ).length;
                return (
                  <div key={sec.id} className="rounded-lg border border-white/10 overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="flex items-center" style={headerStyle}>
                      <button className="flex-1 text-left p-4" onClick={() => toggleExpand(sec.id)}>{expanded[sec.id] ? '▾' : '▸'} <span className="font-semibold">{sec.name}</span>{alarmCount ? <span className="ml-2 text-xs px-2 py-1 rounded bg-sky-500/15 text-sky-300 border border-sky-500/25">{alarmCount} alarms</span> : null}</button>
                      <div className="flex gap-2 pr-4 py-2">
                        <button onClick={() => moveSection(idx, 'up')} disabled={idx === 0} className="px-2 py-1 rounded disabled:opacity-50" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>▲</button>
                        <button onClick={() => moveSection(idx, 'down')} disabled={idx === sections.length - 1} className="px-2 py-1 rounded disabled:opacity-50" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>▼</button>
                        <button onClick={() => startEdit(sec)} className="px-3 py-1 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>Edit</button>
                        <button onClick={() => setConfirmDel(sec)} className="px-3 py-1 rounded text-white" style={{ backgroundColor: '#e11d48' }}>Archive</button>
                      </div>
                    </div>
                    {expanded[sec.id] && <div className="p-4 space-y-3">
                      {groups.map(({ sub, items }, gi) => <div key={`${sub}-${gi}`} className="rounded-lg border border-white/10 p-3" style={{ background: 'rgba(0,0,0,0.15)' }}>
                        <div className="font-semibold mb-2">{sub || '(No subheading)'}</div>
                        <div className="space-y-2">{items.filter((f) => !f.disabled && !f.inactive).map((f, i) => <div key={i} className="flex items-center justify-between gap-3 text-sm rounded p-2 bg-white/5"><span>{f.label || '—'}</span><span className="flex items-center gap-2"><span className="opacity-60">{f.type || '—'}</span>{f?.responseRule?.active && <span className="px-2 py-1 rounded bg-sky-500/15 text-sky-300 border border-sky-500/25">alarm {f.responseRule.dueTime || ''}</span>}</span></div>)}</div>
                      </div>)}
                    </div>}
                  </div>
                );
              })}
              {sections.length === 0 && <div className="opacity-70">No sections for this store yet.</div>}
            </div>
          </div>
        </div>

        <div className="rounded-xl border sticky top-4 self-start" style={cardStyle}>
          {!editing ? <div className="p-5 space-y-4">
            <div>
              <h2 className="text-xl font-semibold">Section Workspace</h2>
              <p className="text-sm opacity-70 mt-1">Select Edit on a standard. Duties and response alarms open here without a pop-up modal.</p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg border border-white/10 p-3" style={{ background: 'rgba(255,255,255,0.03)' }}><div className="opacity-60">Total duties</div><div className="text-2xl font-bold">{allFields.length}</div></div>
              <div className="rounded-lg border border-white/10 p-3" style={{ background: 'rgba(255,255,255,0.03)' }}><div className="opacity-60">Alarm coverage</div><div className="text-2xl font-bold">{alarmCoverage}%</div></div>
            </div>
            <div className="rounded-lg border border-sky-500/20 p-3" style={{ background: 'rgba(14,165,233,0.08)' }}>
              <div className="font-semibold mb-2">Response Setup Queue</div>
              <div className="text-xs opacity-70 mb-3">These duties exist but do not yet have due times, reminders or escalation rules.</div>
              <div className="space-y-2 max-h-72 overflow-auto pr-1">
                {unconfiguredFields.slice(0, 10).map((f, i) => (
                  <div key={`${f.sectionId}-${i}`} className="rounded border border-white/10 p-2 text-sm" style={{ background: 'rgba(0,0,0,0.18)' }}>
                    <div className="font-semibold">{f.label}</div>
                    <div className="text-xs opacity-60">{f.sectionName} • {f.type || 'field'}</div>
                  </div>
                ))}
                {!unconfiguredFields.length && <div className="text-sm opacity-70">All duties in this store have response rules configured.</div>}
              </div>
            </div>
          </div> : <EditWorkspace orgId={orgId} editing={editing} setEditing={setEditing} updateDraftField={updateDraftField} updateResponseRule={updateResponseRule} handleSaveEdit={handleSaveEdit} accentBtn={accentBtn} roles={roles} />}
        </div>
      </div>
    </div>
  );

  
  const renderPerformance = () => {
    const staffNames = selectedActiveStaffNames.length ? selectedActiveStaffNames : activeStaffRows.map(r => r.name).filter(Boolean).slice(0, 6);
    const staffRows = (staffNames.length ? staffNames : ['No active staff']).map((name, i) => ({
      label: name,
      value: name === 'No active staff' ? '0%' : `${Math.max(58, compliance - (i * 4))}%`,
      sub: name === 'No active staff' ? 'Clock-ins connect from Active Staff' : `${Math.max(0, responseFields.length - i)} completed • ${i} delayed`,
    }));
    const siteRows = stores.slice(0, 8).map((s, i) => ({
      label: s.name,
      value: `${Math.max(55, compliance - (i * 3))}%`,
      sub: `${Math.max(0, delayed + i)} delays • ${i % 3} escalations`,
    }));
    const sectionRows = sections.slice(0, 8).map((sec, i) => {
      const duties = (sec.fields || []).filter(f => (f.label || '').trim()).length;
      const alarms = (sec.fields || []).filter(f => f?.responseRule?.active).length;
      const score = duties ? Math.round((alarms / duties) * 100) : 100;
      return { label: sec.name, value: `${score}%`, sub: `${alarms}/${duties} duties protected by alarms` };
    });
    const missedRows = unconfiguredFields.slice(0, 6).map((f, i) => ({ label: f.label || f.sectionName, value: `${6 - Math.min(i,5)} misses`, sub: `${f.sectionName} • needs alarm rules` }));
    const escalationRows = riskRows.filter(r => ['Escalated','Delayed'].includes(r.risk.status)).slice(0, 6).map(r => ({ label: r.label || r.sectionName, value: r.risk.status, sub: `${r.sectionName} • ${r.risk.timeText}` }));
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">{renderStorePicker()}<span className="text-xs px-2 py-1 rounded bg-purple-500/15 text-purple-300 border border-purple-500/25">Performance V5</span></div>
        <div className="rounded-xl border p-5" style={cardStyle}>
          <h2 className="text-xl font-semibold">Performance & Accountability</h2>
          <p className="text-sm opacity-70 mt-1">This view turns completed, delayed and missed duties into staff, site and section accountability. Phase 3 will replace setup-based scores with live Neo App events.</p>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <AccountabilityPanel title="Staff Ranking" rows={staffRows} />
          <AccountabilityPanel title="Needs Attention" rows={staffRows.slice().reverse().slice(0, 4)} warning />
          <AccountabilityPanel title="Most Missed Duties" rows={missedRows.length ? missedRows : [{ label: 'No missed duties yet', value: '0', sub: 'Live history connects in Phase 3' }]} />
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <AccountabilityPanel title="Site Performance" rows={siteRows} />
          <AccountabilityPanel title="Section Performance" rows={sectionRows.length ? sectionRows : [{ label: 'No sections yet', value: '0%', sub: 'Create standards in Builder' }]} />
          <AccountabilityPanel title="Escalation Leaderboard" rows={escalationRows.length ? escalationRows : [{ label: 'No escalations yet', value: '0', sub: 'Escalations appear when duties pass threshold' }]} />
        </div>
      </div>
    );
  };


  const renderResponseCentre = () => {
    const columns = [
      {
        key: 'dueNow',
        title: 'Due Now',
        sub: 'Active duties Neo is currently driving',
        tone: 'sky',
        rows: dutyIntelligenceGroups.dueNow,
      },
      {
        key: 'upcoming',
        title: 'Upcoming',
        sub: 'Planned duties with healthy ownership',
        tone: 'emerald',
        rows: dutyIntelligenceGroups.upcoming,
      },
      {
        key: 'overdue',
        title: 'Overdue',
        sub: 'Past due and still incomplete',
        tone: 'orange',
        rows: dutyIntelligenceGroups.overdue,
      },
      {
        key: 'missingOwner',
        title: 'Missing Owner',
        sub: 'Coverage gap requiring intervention',
        tone: 'rose',
        rows: dutyIntelligenceGroups.missingOwner,
      },
      {
        key: 'escalated',
        title: 'Escalated',
        sub: 'Manager response required',
        tone: 'rose',
        rows: dutyIntelligenceGroups.escalated,
      },
    ];
    return (
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {renderStorePicker()}
          <span className="text-xs px-2 py-1 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/25">
            Duty Intelligence Dashboard
          </span>
        </div>
        <div
          className={`rounded-xl border p-5 ${
            responseCentreKpis.attention > 0
              ? 'border-orange-400/30 bg-orange-400/5'
              : 'border-emerald-400/25 bg-emerald-400/5'
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className={`text-xs font-bold uppercase tracking-[0.16em] ${
                responseCentreKpis.attention > 0 ? 'text-orange-300' : 'text-emerald-300'
              }`}>
                Neo Manager Intelligence
              </div>
              <h2 className="mt-1 text-xl font-semibold">
                {executionAssurance.attention > 0
                  ? `${executionAssurance.attention} item${executionAssurance.attention === 1 ? '' : 's'} need management attention`
                  : executionAssurance.handled > 0
                  ? `Neo is handling ${executionAssurance.handled} active item${executionAssurance.handled === 1 ? '' : 's'} · no manager action required`
                  : 'Neo is quiet · operations normal'}
              </h2>
              <p className="text-sm opacity-70 mt-1">
                {executionAssurance.attention > 0
                  ? 'Only exceptions requiring a human response are elevated here.'
                  : executionAssurance.handled > 0
                  ? 'Reminders, attendance checks and automatic coverage are running without management intervention.'
                  : 'No overdue duties, missing owners, attendance gaps or unresolved escalations are currently detected.'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full border border-emerald-400/20 bg-emerald-400/5 px-2.5 py-1 text-emerald-200">
                Healthy {executionAssurance.healthy}
              </span>
              <span className="rounded-full border border-cyan-400/20 bg-cyan-400/5 px-2.5 py-1 text-cyan-200">
                Neo handling {executionAssurance.handled}
              </span>
              <span className="rounded-full border border-orange-400/20 bg-orange-400/5 px-2.5 py-1 text-orange-200">
                Needs attention {executionAssurance.attention}
              </span>
            </div>
            <span className={`rounded-full border px-3 py-1 text-xs font-bold ${
              responseCentreKpis.attention > 0
                ? 'border-orange-400/30 bg-orange-400/10 text-orange-200'
                : 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
            }`}>
              {responseCentreKpis.attention > 0 ? 'Attention required' : 'All clear'}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className={kpiCard} style={cardStyle}>
            <span className="text-xs uppercase tracking-wider text-sky-300">Due Now</span>
            <strong className="text-3xl">{executionAssurance.dueNow}</strong>
          </div>
          <div className={kpiCard} style={cardStyle}>
            <span className="text-xs uppercase tracking-wider text-emerald-300">Upcoming</span>
            <strong className="text-3xl">{executionAssurance.upcoming}</strong>
          </div>
          <div className={kpiCard} style={cardStyle}>
            <span className="text-xs uppercase tracking-wider text-orange-300">Overdue</span>
            <strong className="text-3xl text-orange-300">{executionAssurance.overdue}</strong>
          </div>
          <div className={kpiCard} style={cardStyle}>
            <span className="text-xs uppercase tracking-wider text-rose-300">Missing Owner</span>
            <strong className="text-3xl text-rose-300">{executionAssurance.missingOwner}</strong>
          </div>
          <div className={kpiCard} style={cardStyle}>
            <span className="text-xs uppercase tracking-wider text-rose-300">Escalated</span>
            <strong className="text-3xl text-rose-300">{executionAssurance.escalated}</strong>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <ManagerInboxPanel title="Needs Attention" sub="Only exceptions requiring a manager decision or intervention" rows={managerInboxGroups.newAlerts} updateQueueItem={updateQueueItem} empty="Neo is quiet. No manager action required." />
          <ManagerInboxPanel title="Acknowledged" sub="Manager has seen these alerts" rows={managerInboxGroups.acknowledged} updateQueueItem={updateQueueItem} empty="No acknowledged alerts." compact />
          <NotificationLogPanel rows={notificationLogRows} />
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-white/60">
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
            Acknowledged {responseCentreKpis.acknowledged}
          </span>
          <span className="rounded-full border border-emerald-400/15 bg-emerald-400/5 px-3 py-1.5 text-emerald-200">
            Resolved {responseCentreKpis.resolved}
          </span>
          <span className="rounded-full border border-cyan-400/15 bg-cyan-400/5 px-3 py-1.5 text-cyan-200">
            Neo handling {executionAssurance.handled}
          </span>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.025] px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">
                Sprint 7 Execution Assurance
              </div>
              <div className="mt-1 text-sm text-white/70">
                Quiet means healthy. Staff reminders stay with staff; only unresolved operational exceptions reach management.
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full border border-emerald-400/20 bg-emerald-400/5 px-2.5 py-1 text-emerald-200">
                Silent {s7ScenarioAssurance.healthy}
              </span>
              <span className="rounded-full border border-cyan-400/20 bg-cyan-400/5 px-2.5 py-1 text-cyan-200">
                Staff response {s7ScenarioAssurance.staffResponse}
              </span>
              <span className="rounded-full border border-orange-400/20 bg-orange-400/5 px-2.5 py-1 text-orange-200">
                Manager {s7ScenarioAssurance.managerAttention}
              </span>
              <span className="rounded-full border border-sky-400/20 bg-sky-400/5 px-2.5 py-1 text-sky-200">
                Reassigned {s7ScenarioAssurance.reassigned}
              </span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
          {columns.map(col => (
            <div
              key={col.key}
              className={`rounded-xl border overflow-hidden ${
                col.key === 'missingOwner' || col.key === 'escalated'
                  ? 'border-rose-400/20'
                  : col.key === 'overdue'
                  ? 'border-orange-400/20'
                  : col.key === 'dueNow'
                  ? 'border-sky-400/20'
                  : 'border-emerald-400/15'
              }`}
              style={{ backgroundColor: 'var(--brand-surface)' }}
            >
              <div className="p-4 border-b border-white/10" style={headerStyle}>
                <div className="flex items-center justify-between gap-2"><h3 className="font-semibold">{col.title}</h3><span className="text-sm font-bold">{col.rows.length}</span></div>
                <div className="text-xs opacity-60 mt-1">{col.sub}</div>
              </div>
              <div className="p-3 space-y-3 max-h-[520px] overflow-auto">
                {col.rows.length === 0 && (
                  <div className="rounded-lg border border-dashed border-white/10 p-4 text-sm opacity-60">
                    {col.key === 'upcoming'
                      ? 'No upcoming duties.'
                      : col.key === 'missingOwner'
                      ? 'No coverage gaps.'
                      : col.key === 'escalated'
                      ? 'No escalations.'
                      : col.key === 'overdue'
                      ? 'No overdue duties.'
                      : 'No duties due now.'}
                  </div>
                )}
                {col.rows.map(item => <ResponseCentreCard key={item.id} item={item} updateQueueItem={updateQueueItem} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSettings = () => {
    const staffTemplateReady = !!responseSettings.whatsappDutyDueTemplate;
    const managerTemplateReady = !!responseSettings.whatsappManagerEscalationTemplate;
    const quietHoursConfigured = !!(responseSettings.quietHoursStart && responseSettings.quietHoursEnd);

    const statusPill = (label, tone = 'neutral') => {
      const tones = {
        live: { bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.32)', color: '#6ee7b7' },
        pending: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)', color: '#fbbf24' },
        info: { bg: 'rgba(56,189,248,0.10)', border: 'rgba(56,189,248,0.30)', color: '#7dd3fc' },
        neutral: { bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.22)', color: '#cbd5e1' },
      };
      const palette = tones[tone] || tones.neutral;
      return (
        <span
          className="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold"
          style={{ background: palette.bg, borderColor: palette.border, color: palette.color }}
        >
          {label}
        </span>
      );
    };

    return (
      <div className="space-y-5">
        <div
          className="rounded-2xl border p-5 md:p-6"
          style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, rgba(7,20,38,.98), rgba(15,38,66,.96))',
          }}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">Response Rules</div>
              <h2 className="mt-1 text-2xl font-bold">How Neo responds when work is due</h2>
              <p className="mt-2 text-sm opacity-70">
                Keep this simple: duties define their own due time and grace period. This page controls the communication channels Neo uses when staff need a reminder or management needs to intervene.
              </p>
            </div>
            {statusPill('Execution assurance live', 'live')}
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-white/10 p-3">
              <div className="text-xs uppercase tracking-wider opacity-60">1 · Duty scheduled</div>
              <div className="mt-1 font-semibold">Standard owns timing</div>
              <div className="mt-1 text-xs opacity-60">Due time, reminder-before and grace are configured on each duty.</div>
            </div>
            <div className="rounded-xl border border-white/10 p-3">
              <div className="text-xs uppercase tracking-wider opacity-60">2 · Staff reminder</div>
              <div className="mt-1 font-semibold">WhatsApp to assigned staff</div>
              <div className="mt-1 text-xs opacity-60">Schedule Intelligence resolves the person from published shift + role + duty ownership.</div>
            </div>
            <div className="rounded-xl border border-white/10 p-3">
              <div className="text-xs uppercase tracking-wider opacity-60">3 · Grace expires</div>
              <div className="mt-1 font-semibold">Exception becomes overdue</div>
              <div className="mt-1 text-xs opacity-60">Neo keeps chasing the unresolved duty and prepares management escalation.</div>
            </div>
            <div className="rounded-xl border border-white/10 p-3">
              <div className="text-xs uppercase tracking-wider opacity-60">4 · Manager notified</div>
              <div className="mt-1 font-semibold">Verified manager receives WhatsApp</div>
              <div className="mt-1 text-xs opacity-60">The manager is resolved from the Managers registry for the duty site.</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <SettingsCard title="Staff Duty Reminders">
            <div className="flex items-start justify-between gap-3 rounded-xl border border-cyan-500/20 p-4" style={{ background: 'rgba(34,211,238,0.05)' }}>
              <div>
                <div className="font-semibold">WhatsApp duty reminder</div>
                <div className="mt-1 text-xs opacity-65">
                  Uses the approved Meta template <span className="font-semibold">neo_duty_due</span>.
                </div>
              </div>
              {statusPill('S7.5 routing live', 'live')}
            </div>

            <div className="rounded-xl border border-white/10 p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold">Channel</div>
                  <div className="text-xs opacity-60">Allow Neo to send duty-due WhatsApp messages when a staff recipient can be resolved.</div>
                </div>
                <input
                  type="checkbox"
                  checked={!!responseSettings.whatsappDutyDueEnabled}
                  onChange={e => setResponseSettings(s => ({ ...s, whatsappDutyDueEnabled: e.target.checked }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-white/10 p-3">
                  <div className="text-xs uppercase tracking-wider opacity-55">Template</div>
                  <div className="mt-1 font-semibold">{responseSettings.whatsappDutyDueTemplate || 'neo_duty_due'}</div>
                  <div className="mt-1">{staffTemplateReady ? statusPill('Configured', 'live') : statusPill('Template missing', 'pending')}</div>
                </div>
                <div className="rounded-lg border border-white/10 p-3">
                  <div className="text-xs uppercase tracking-wider opacity-55">Recipient logic</div>
                  <div className="mt-1 font-semibold">Scheduled role owner</div>
                  <div className="mt-1 text-xs opacity-60">Published schedule + live availability + duty role resolve the current staff recipient.</div>
                </div>
              </div>

              <details className="rounded-lg border border-white/10 p-3">
                <summary className="cursor-pointer text-sm font-semibold">Staff WhatsApp template settings</summary>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="space-y-1">
                    <span className="text-xs opacity-70">Meta template name</span>
                    <input className="w-full p-2 rounded border border-white/10" style={inputStyle} value={responseSettings.whatsappDutyDueTemplate || 'neo_duty_due'} onChange={e => setResponseSettings(s => ({ ...s, whatsappDutyDueTemplate: e.target.value }))} />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs opacity-70">Language</span>
                    <input className="w-full p-2 rounded border border-white/10" style={inputStyle} value={responseSettings.whatsappDutyDueLanguage || 'en'} onChange={e => setResponseSettings(s => ({ ...s, whatsappDutyDueLanguage: e.target.value }))} />
                  </label>
                  <label className="space-y-1 md:col-span-2">
                    <span className="text-xs opacity-70">Neo staff URL</span>
                    <input className="w-full p-2 rounded border border-white/10" style={inputStyle} value={responseSettings.neoStaffAppUrl || 'https://neo.matrixos.co.za'} onChange={e => setResponseSettings(s => ({ ...s, neoStaffAppUrl: e.target.value }))} />
                  </label>
                </div>
              </details>
            </div>
          </SettingsCard>

          <SettingsCard title="Manager Escalation">
            <div className="flex items-start justify-between gap-3 rounded-xl border border-emerald-500/20 p-4" style={{ background: 'rgba(16,185,129,0.05)' }}>
              <div>
                <div className="font-semibold">Management WhatsApp escalation</div>
                <div className="mt-1 text-xs opacity-65">
                  Proven live: Neo resolves a verified manager for the duty site after the duty grace period.
                </div>
              </div>
              {statusPill('Live', 'live')}
            </div>

            <div className="rounded-xl border border-white/10 p-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">WhatsApp escalation enabled</div>
                  <div className="mt-1 text-xs opacity-60">Turn this off only if you intentionally want Neo to stop management WhatsApp escalations.</div>
                </div>
                <input
                  type="checkbox"
                  checked={responseSettings.whatsappManagerEscalationEnabled !== false}
                  onChange={e => setResponseSettings(s => ({ ...s, whatsappManagerEscalationEnabled: e.target.checked }))}
                />
              </div>

              <div className="rounded-lg border border-white/10 p-3">
                <div className="text-xs uppercase tracking-wider opacity-55">Who gets notified?</div>
                <div className="mt-1 font-semibold">Verified manager for the duty's store</div>
                <div className="mt-1 text-xs opacity-60">
                  Manage names, stores and verified Response Identities from the <span className="font-semibold">Management</span> page.
                </div>
              </div>

              <div className="rounded-lg border border-white/10 p-3">
                <div className="text-xs uppercase tracking-wider opacity-55">When?</div>
                <div className="mt-1 font-semibold">After the duty-specific grace period</div>
                <div className="mt-1 text-xs opacity-60">
                  The grace period configured on the duty is the primary escalation window. The default below is only a fallback where a duty has no explicit grace.
                </div>
              </div>

              <label className="space-y-1">
                <span className="text-xs opacity-70">Fallback escalation delay</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    className="w-full p-2 rounded border border-white/10"
                    style={inputStyle}
                    value={responseSettings.managerEscalationAfterMins ?? 30}
                    onChange={e => setResponseSettings(s => ({ ...s, managerEscalationAfterMins: Math.max(1, Number(e.target.value || 1)) }))}
                  />
                  <span className="text-xs opacity-60">minutes</span>
                </div>
              </label>

              <details className="rounded-lg border border-white/10 p-3">
                <summary className="cursor-pointer text-sm font-semibold">Manager WhatsApp template settings</summary>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="space-y-1">
                    <span className="text-xs opacity-70">Meta template</span>
                    <input className="w-full p-2 rounded border border-white/10" style={inputStyle} value={responseSettings.whatsappManagerEscalationTemplate || 'neo_manager_escalation'} onChange={e => setResponseSettings(s => ({ ...s, whatsappManagerEscalationTemplate: e.target.value }))} />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs opacity-70">Language</span>
                    <input className="w-full p-2 rounded border border-white/10" style={inputStyle} value={responseSettings.whatsappManagerEscalationLanguage || 'en'} onChange={e => setResponseSettings(s => ({ ...s, whatsappManagerEscalationLanguage: e.target.value }))} />
                  </label>
                  <label className="space-y-1 md:col-span-2">
                    <span className="text-xs opacity-70">Neo manager URL</span>
                    <input className="w-full p-2 rounded border border-white/10" style={inputStyle} value={responseSettings.neoAdminUrl || 'https://neo.matrixos.co.za'} onChange={e => setResponseSettings(s => ({ ...s, neoAdminUrl: e.target.value }))} />
                  </label>
                </div>
                <div className="mt-3 text-xs opacity-60">
                  Template variables: Manager Name, Duty, Site, Due Time, Overdue Duration, Assigned Staff and Neo URL.
                </div>
              </details>
            </div>
          </SettingsCard>

          <SettingsCard title="Notification Window">
            <div className="rounded-xl border border-white/10 p-4">
              <div className="font-semibold">Quiet hours</div>
              <div className="mt-1 text-xs opacity-60">Optional. Use this only if routine notifications should be suppressed during a store's closed period.</div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs opacity-70">Starts</span>
                  <input type="time" className="w-full p-2 rounded border border-white/10" style={inputStyle} value={responseSettings.quietHoursStart} onChange={e => setResponseSettings(s => ({ ...s, quietHoursStart: e.target.value }))} />
                </label>
                <label className="space-y-1">
                  <span className="text-xs opacity-70">Ends</span>
                  <input type="time" className="w-full p-2 rounded border border-white/10" style={inputStyle} value={responseSettings.quietHoursEnd} onChange={e => setResponseSettings(s => ({ ...s, quietHoursEnd: e.target.value }))} />
                </label>
              </div>

              <div className="mt-3">
                {quietHoursConfigured ? statusPill(`${responseSettings.quietHoursStart} – ${responseSettings.quietHoursEnd}`, 'info') : statusPill('No quiet hours', 'neutral')}
              </div>
            </div>
          </SettingsCard>

          <SettingsCard title="Where recipients are managed">
            <div className="space-y-3">
              <div className="rounded-xl border border-white/10 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold">Management</div>
                    <div className="mt-1 text-xs opacity-60">Management People, site assignment, WhatsApp Response Identity and preferred response channel.</div>
                  </div>
                  {statusPill('Manager registry', 'live')}
                </div>
              </div>
              <div className="rounded-xl border border-white/10 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold">Staff</div>
                    <div className="mt-1 text-xs opacity-60">Staff role, store and verified WhatsApp Response Identity.</div>
                  </div>
                  {statusPill('Staff registry', 'info')}
                </div>
              </div>
              <div className="rounded-xl border border-amber-500/20 p-4" style={{ background: 'rgba(245,158,11,0.05)' }}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold">Schedule Intelligence</div>
                    <div className="mt-1 text-xs opacity-60">Live: resolves exactly which scheduled staff member owns a duty by role and shift.</div>
                  </div>
                  {statusPill('Next', 'pending')}
                </div>
              </div>
            </div>
          </SettingsCard>
        </div>

        <div className="rounded-2xl border p-4 md:p-5" style={cardStyle}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-semibold">Save response configuration</div>
              <div className="mt-1 text-xs opacity-60">Duty timing remains configured inside each Standard. These settings control Neo's communication channels and fallback behaviour.</div>
            </div>
            <div className="flex items-center gap-3">
              {settingsSaved && <span className="px-3 py-1 rounded text-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Settings saved</span>}
              <button onClick={saveSettings} className="px-4 py-2 rounded font-semibold" style={accentBtn}>Save Response Rules</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTimeline = () => (
    <div className="space-y-5">
      {renderStorePicker()}
      <div className="rounded-xl border p-5" style={cardStyle}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">Duty Timeline</h2>
            <p className="text-sm opacity-70 mt-1">V5 previews the exact reminder path for every configured alarm. In Phase 3, Neo App completion events will replace the preview with live proof, staff member and evidence history.</p>
          </div>
          <span className="text-xs px-2 py-1 rounded bg-sky-500/15 text-sky-300 border border-sky-500/25">Timeline Preview V5</span>
        </div>
      </div>
      <div className="rounded-xl border p-4" style={cardStyle}>
        <div className="space-y-3">
          {(riskRows.length ? riskRows : responseFields).slice(0, 12).map((r, i) => {
            const steps = timelineStepsForRule(r.responseRule);
            return (
              <div key={r.rowId || i} className="rounded-lg border border-white/10 p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold">{r.sectionName} → {r.label || 'Duty'}</div>
                    <div className="text-xs opacity-60 mt-1">Assigned: {selectedActiveStaffNames.length ? selectedActiveStaffNames.join(', ') : 'No active staff'} • Method: {r.responseRule?.escalationMethod || 'email'}</div>
                  </div>
                  <RiskBadge tone={(r.risk || riskStatusForRule(r.responseRule) || {}).tone || 'green'} label={(r.risk || riskStatusForRule(r.responseRule) || {}).status || 'Scheduled'} />
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                  {steps.map((step, idx) => <TimelinePill key={`${step.label}-${idx}`} label={`${step.label} • ${step.at}`} value={`${step.state === 'triggered' ? 'Done / passed' : 'Waiting'} — ${step.detail}`} />)}
                  <TimelinePill label="Completion" value="Waiting for Neo App submission" />
                </div>
              </div>
            );
          })}
          {responseFields.length === 0 && <div className="space-y-3"><div className="opacity-70">Configure response alarms in Builder to preview duty timelines here.</div>{unconfiguredFields.slice(0, 8).map((f, i) => <div key={`${f.sectionId}-empty-${i}`} className="rounded-lg border border-white/10 p-3 text-sm" style={{ background: 'rgba(255,255,255,0.03)' }}><span className="font-semibold">{f.sectionName}</span> → {f.label}<span className="ml-2 opacity-60">needs due time</span></div>)}</div>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6 text-white min-h-screen" style={{ backgroundColor: 'var(--brand-bg)' }}>
      <div><h1 className="text-3xl font-bold">Neo Command Centre</h1><p className="text-sm opacity-70 mt-1">Live operations, standards, performance, response rules and accountability.</p></div>
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-2">
        {TABS.map(tab => {
          const label = tab === 'overview' ? 'Live Operations' : tab === 'builder' ? 'Standards' : tab === 'response' ? 'Response Centre' : tab === 'performance' ? 'Performance' : tab === 'settings' ? 'Rules & Templates' : 'Timeline';
          return <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-t-lg font-semibold ${activeTab === tab ? 'bg-white/15 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`}>{label}</button>;
        })}
      </div>
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'builder' && renderBuilder()}
      {activeTab === 'response' && renderResponseCentre()}
      {activeTab === 'performance' && renderPerformance()}
      {activeTab === 'settings' && renderSettings()}
      {activeTab === 'timeline' && renderTimeline()}

      <Confirm open={!!confirmDel} title="Archive section" message={`Archive “${confirmDel?.name || ''}”? If this section has submissions it will move to Archived. If it has never been used it will be removed permanently.`} onCancel={() => setConfirmDel(null)} onOk={() => handleDelete(confirmDel)} okLabel="Archive" variant="danger" />
      <Confirm open={dedupeOpen} title="Remove duplicates" message="This will remove duplicate sections in the selected store. If a duplicate has submissions, it will be archived instead of deleted. Continue?" onCancel={() => setDedupeOpen(false)} onOk={async () => { setDedupeOpen(false); await dedupeCurrentStore(); }} okLabel="Remove duplicates" />
    </div>
  );
}



function ManagerInboxPanel({ title, sub, rows = [], updateQueueItem, empty, compact = false }) {
  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--brand-surface)', borderColor: 'rgba(255,255,255,0.08)' }}>
      <div className="p-4 border-b border-white/10" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
        <div className="flex items-center justify-between gap-2"><h3 className="font-semibold">{title}</h3><span className="text-sm font-bold">{rows.length}</span></div>
        <div className="text-xs opacity-60 mt-1">{sub}</div>
      </div>
      <div className="p-3 space-y-3 max-h-[320px] overflow-auto">
        {rows.length === 0 && <div className="rounded-lg border border-dashed border-white/10 p-4 text-sm opacity-60">{empty}</div>}
        {rows.map(item => <ResponseCentreCard key={item.id} item={item} updateQueueItem={updateQueueItem} compact={compact} />)}
      </div>
    </div>
  );
}

function NotificationLogPanel({ rows = [] }) {
  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--brand-surface)', borderColor: 'rgba(255,255,255,0.08)' }}>
      <div className="p-4 border-b border-white/10" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
        <div className="flex items-center justify-between gap-2"><h3 className="font-semibold">Notification Log</h3><span className="text-sm font-bold">{rows.length}</span></div>
        <div className="text-xs opacity-60 mt-1">Dashboard/email/push delivery history foundation.</div>
      </div>
      <div className="p-3 space-y-2 max-h-[320px] overflow-auto">
        {rows.length === 0 && <div className="rounded-lg border border-dashed border-white/10 p-4 text-sm opacity-60">No notification events yet.</div>}
        {rows.map(row => (
          <div key={row.id} className="rounded-lg border border-white/10 p-3 text-xs" style={{ background: 'rgba(255,255,255,0.03)' }}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-sm">{row.dutyLabel || 'Duty'}</div>
                <div className="opacity-60 mt-1">{row.storeName || 'Selected site'} • {row.deliveryMethod || 'dashboard'} • {row.targetRole || 'manager'}</div>
              </div>
              <span className="uppercase px-2 py-1 rounded bg-black/20 border border-white/10">{row.status || 'new'}</span>
            </div>
            <div className="mt-2 opacity-60">{formatTimeValue(row.createdAt) || 'Just now'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResponseCentreCard({ item, updateQueueItem, compact = false }) {
  const status = item.computedStatus || queueItemStatus(item);
  const overdueMins = minutesBetweenNow(item.dueAt);
  const toneClass = status === 'resolved'
    ? 'border-emerald-500/30 bg-emerald-500/5'
    : status === 'acknowledged'
      ? 'border-cyan-500/35 bg-cyan-500/10'
      : status === 'escalated'
        ? 'border-rose-500/40 bg-rose-500/10'
        : status === 'overdue'
          ? 'border-orange-500/35 bg-orange-500/10'
          : 'border-sky-500/30 bg-sky-500/10';
  const label = item.dutyLabel || item.label || item.dutyName || 'Duty';
  const site = item.storeName || item.siteName || 'Selected site';
  const assigned =
    item.completedBy ||
    item.assignedStaffName ||
    item.responseOwner ||
    item.ownerName ||
    item.assignedTo ||
    item.actorName ||
    'Unassigned';
  const managerIntel = item.managerIntel || managerResponseIntel({ ...item, computedStatus: status });
  const isAcknowledged = !!item.managerAcknowledged || item.managerStatus === 'acknowledged' || status === 'acknowledged';
  return (
    <div className={`rounded-lg border p-3 text-sm ${toneClass}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold leading-tight">{label}</div>
          <div className="text-xs opacity-70 mt-1">{site}</div>
        </div>
        <span className="text-[11px] uppercase tracking-wide px-2 py-1 rounded bg-black/20 border border-white/10">{status}</span>
      </div>
      {status !== 'resolved' && (
        <div className={`mt-3 rounded-lg border px-2.5 py-2 text-xs ${
          managerIntel.needsManager
            ? managerIntel.severity === 'red'
              ? 'border-rose-400/25 bg-rose-400/8 text-rose-100'
              : 'border-orange-400/25 bg-orange-400/8 text-orange-100'
            : managerIntel.severity === 'blue'
            ? 'border-cyan-400/20 bg-cyan-400/5 text-cyan-100'
            : 'border-emerald-400/20 bg-emerald-400/5 text-emerald-100'
        }`}>
          <div className="font-bold">{managerIntel.headline}</div>
          <div className="mt-1 opacity-80">{managerIntel.explanation}</div>
          <div className="mt-1 font-semibold">Recommended: {managerIntel.action}</div>
        </div>
      )}
      {!compact && <div className="mt-3 grid grid-cols-2 gap-2 text-xs opacity-80">
        <div><span className="opacity-60">Due</span><div className="font-semibold">{item.dueTime || formatTimeValue(item.dueAt)}</div></div>
        <div><span className="opacity-60">Owner</span><div className="font-semibold truncate">{assigned}</div></div>
        <div><span className="opacity-60">Overdue</span><div className="font-semibold">{status === 'resolved' ? 'Resolved' : overdueMins != null ? `${overdueMins}m` : '—'}</div></div>
        <div><span className="opacity-60">Escalation</span><div className="font-semibold truncate">{item.escalationLevel || item.escalationStatus || 'Level 1'}</div></div>
      </div>}
      {item.dueNotification?.status && (
        <div className={`mt-2 text-xs font-semibold ${
          item.dueNotification.status === 'sent' ? 'text-emerald-300' :
          item.dueNotification.status === 'failed' ? 'text-rose-300' :
          item.dueNotification.status === 'partial' ? 'text-amber-300' :
          'text-cyan-200'
        }`}>
          WhatsApp: {String(item.dueNotification.status).replaceAll('_', ' ')}
          {Number(item.dueNotification.sentCount || 0) > 0 ? ` • ${item.dueNotification.sentCount}/${item.dueNotification.recipientCount || item.dueNotification.sentCount} sent` : ''}
          {item.dueNotification.reason ? ` • ${item.dueNotification.reason}` : ''}
        </div>
      )}
      {item.managerEscalation?.status && (
        <div className={`mt-2 text-xs font-semibold ${
          item.managerEscalation.status === 'sent' ? 'text-rose-300' :
          item.managerEscalation.status === 'failed' ? 'text-rose-400' :
          item.managerEscalation.status === 'queued' ? 'text-amber-300' :
          'text-cyan-200'
        }`}>
          Management: {String(item.managerEscalation.status).replaceAll('_', ' ')}
          {item.managerEscalation.managerName ? ` • ${item.managerEscalation.managerName}` : ''}
          {item.managerEscalation.overdueMins ? ` • ${item.managerEscalation.overdueMins}m overdue` : ''}
          {item.managerEscalation.error ? ` • ${item.managerEscalation.error}` : ''}
        </div>
      )}
      {item.attendanceStatus && status !== 'resolved' && (
        <div className={`mt-2 rounded-lg border px-2.5 py-2 text-xs ${
          ['expected_owner_not_clocked_in', 'owner_left_before_due'].includes(item.attendanceStatus)
            ? 'border-rose-400/25 bg-rose-400/5 text-rose-100'
            : item.attendanceStatus === 'covered_by_replacement'
            ? 'border-cyan-400/20 bg-cyan-400/5 text-cyan-100'
            : item.attendanceStatus === 'owner_on_shift'
            ? 'border-emerald-400/20 bg-emerald-400/5 text-emerald-100'
            : 'border-white/10 bg-white/5 text-white/70'
        }`}>
          <span className="font-bold">Attendance</span>
          {item.attendanceReason ? ` · ${item.attendanceReason}` : ''}
        </div>
      )}
      {item.ownershipResolutionStatus === 'reassigned' && status !== 'resolved' && (
        <div className="mt-2 rounded-lg border border-cyan-400/20 bg-cyan-400/5 px-2.5 py-2 text-xs text-cyan-100">
          <span className="font-bold">Auto reassigned</span>
          {(item.reassignedFromStaffName || item.plannedOwnerStaffName)
            ? ` · ${item.reassignedFromStaffName || item.plannedOwnerStaffName}`
            : ''}
          {(item.assignedStaffName || item.reassignedToStaffName)
            ? ` → ${item.assignedStaffName || item.reassignedToStaffName}`
            : ''}
          {item.reassignmentReason ? ` · ${item.reassignmentReason}` : ''}
        </div>
      )}
      {(item.staffReminderStatus || item.staffReminder?.status) && status !== 'resolved' && (
        <div className={`mt-2 text-xs font-semibold ${
          ['sent', 'delivered'].includes(String(item.staffReminderStatus || item.staffReminder?.status))
            ? 'text-emerald-300'
            : ['queued', 'sending'].includes(String(item.staffReminderStatus || item.staffReminder?.status))
            ? 'text-amber-300'
            : 'text-rose-300'
        }`}>
          Staff WhatsApp: {String(item.staffReminderStatus || item.staffReminder?.status).replaceAll('_', ' ')}
          {(item.staffReminderPhase || item.staffReminder?.phase)
            ? ` • ${String(item.staffReminderPhase || item.staffReminder?.phase).replaceAll('_', ' ')}`
            : ''}
          {Number(item.staffReminderSequence || item.staffReminder?.sequence || 0) > 0
            ? ` #${item.staffReminderSequence || item.staffReminder?.sequence}`
            : ''}
          {(item.staffReminderRecipientName || item.staffReminder?.recipientName)
            ? ` • ${item.staffReminderRecipientName || item.staffReminder?.recipientName}`
            : ''}
          {(item.staffReminderError || item.staffReminder?.error)
            ? ` • ${item.staffReminderError || item.staffReminder?.error}`
            : ''}
        </div>
      )}
      {item.lifecycleReason && status !== 'resolved' && (
        <div className={`mt-2 text-xs font-semibold ${
          item.lifecycleStatus === 'escalated' || item.lifecycleStatus === 'overdue' || item.lifecycleStatus === 'owner_unavailable'
            ? 'text-rose-200'
            : item.lifecycleStatus === 'reassigned'
            ? 'text-cyan-200'
            : 'text-white/55'
        }`}>
          {item.lifecycleReason}
        </div>
      )}
      {item.completedAt && <div className="mt-2 text-xs text-emerald-300">Completed {formatTimeValue(item.completedAt)}{item.completedBy ? ` by ${item.completedBy}` : ''}</div>}
      {isAcknowledged && status !== 'resolved' && <div className="mt-2 text-xs text-cyan-200 font-semibold">Owned by {assigned}.</div>}
      {item.escalationStatus === 'ready' && status !== 'resolved' && !isAcknowledged && <div className="mt-2 text-xs text-rose-200 font-semibold">Manager escalation ready.</div>}
      {status !== 'resolved' && managerIntel.needsManager && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!isAcknowledged && <button type="button" onClick={() => updateQueueItem(item, { managerAcknowledged: true, managerStatus: 'acknowledged' }, 'Alert acknowledged and assigned to you')} className="px-2 py-1 rounded border border-white/10 bg-white/10 hover:bg-white/15 text-xs font-semibold">Acknowledge</button>}
          {isAcknowledged && <span className="px-2 py-1 rounded border border-cyan-500/25 bg-cyan-500/10 text-xs font-semibold text-cyan-100">Acknowledged</span>}
          <button type="button" onClick={() => updateQueueItem(item, { escalationStatus: 'ready', managerStatus: 'escalated_now', escalatedAt: serverTimestamp() }, 'Escalation marked ready')} className="px-2 py-1 rounded border border-rose-500/30 bg-rose-500/15 hover:bg-rose-500/25 text-xs font-semibold text-rose-100">Escalate now</button>
        </div>
      )}
    </div>
  );
}

function RiskBadge({ tone, label }) {
  const styles = {
    red: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    orange: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
    blue: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    green: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  };
  return <span className={`px-2 py-1 rounded border text-xs font-semibold ${styles[tone] || styles.green}`}>{label}</span>;
}

function AccountabilityCard({ title, rows }) {
  return (
    <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--brand-surface)', borderColor: 'rgba(255,255,255,0.08)' }}>
      <h2 className="text-xl font-semibold mb-3">{title}</h2>
      <div className="space-y-2">
        {(rows || []).length === 0 && <div className="text-sm opacity-60">No data yet</div>}
        {(rows || []).map((row, i) => <div key={`${row.label}-${i}`} className="flex items-center justify-between rounded p-3 border border-white/10" style={{ background: 'rgba(255,255,255,0.03)' }}><span>{row.label}</span><span className="font-semibold">{row.value}</span></div>)}
      </div>
    </div>
  );
}


function AccountabilityPanel({ title, rows = [], warning = false }) {
  return (
    <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--brand-surface)', borderColor: 'rgba(255,255,255,0.08)' }}>
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={`${title}-${i}`} className="rounded-lg border border-white/10 p-3" style={{ background: warning ? 'rgba(244,63,94,0.07)' : 'rgba(255,255,255,0.03)' }}>
            <div className="flex items-center justify-between gap-3"><span className="font-semibold">{r.label}</span><span className="font-bold">{r.value}</span></div>
            {r.sub && <div className="text-xs opacity-60 mt-1">{r.sub}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsCard({ title, children }) {
  return (
    <div className="rounded-xl border p-5 space-y-4" style={{ backgroundColor: 'var(--brand-surface)', borderColor: 'rgba(255,255,255,0.08)' }}>
      <h2 className="text-xl font-semibold">{title}</h2>
      {children}
    </div>
  );
}

function TimelinePill({ label, value }) {
  return <div className="rounded border border-white/10 p-3" style={{ background: 'rgba(255,255,255,0.03)' }}><div className="text-xs opacity-60">{label}</div><div className="font-semibold mt-1">{value}</div></div>;
}



function EditWorkspace({ orgId, editing, setEditing, updateDraftField, updateResponseRule, handleSaveEdit, accentBtn, roles = [] }) {
  const [editorStep, setEditorStep] = useState('build');
  const [openMenu, setOpenMenu] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [dutyDropTarget, setDutyDropTarget] = useState(null);
  const [groupDragItem, setGroupDragItem] = useState(null);
  const [groupDropTarget, setGroupDropTarget] = useState(null);
  // Builder UX: sub-headings start collapsed so large standards are scannable.
  // Selection is local to the draft editor and never writes until Publish.
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [selectedDuties, setSelectedDuties] = useState({});
  const [dutyModal, setDutyModal] = useState(null);
  const [morpheusModal,setMorpheusModal]=useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [saveState, setSaveState] = useState({ status: 'saved', text: editing?.hasLocalDraft ? 'Draft restored' : 'Draft saved' });
  const firstAutoSave = useRef(true);
  const saveTimer = useRef(null);

  useEffect(() => {
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('neo-standard-editor-open');
    return () => {
      document.body.style.overflow = oldOverflow;
      document.body.classList.remove('neo-standard-editor-open');
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const groups = editing.draftFields || [];
  const visibleGroups = groups.map((g, gi) => ({ ...g, gi, fields: (g.fields || []).map((f, fi) => ({ ...f, fi })).filter(f => !f.archived) })).filter(g => !g.archived);
  const allDuties = visibleGroups.flatMap((g) => (g.fields || []).map((f) => ({ ...f, gi: g.gi, fi: f.fi, subheading: g.subheading })));
  const totalDuties = allDuties.filter(f => (f.label || '').trim()).length;
  const activeDuties = allDuties.filter(f => (f.label || '').trim() && !f.disabled).length;
  const activeAlarms = allDuties.filter(f => !f.disabled && f?.responseRule?.active).length;
  const coverage = activeDuties ? Math.round((activeAlarms / activeDuties) * 100) : 0;
  const sectionAggregateTarget = normalizeAggregateResponseTarget(editing.sectionResponseTarget);
  const activeAggregateTargets =
    (sectionAggregateTarget.active ? 1 : 0) +
    visibleGroups.reduce((sum, g) => {
      const key = String(g.subheading || '').trim() || 'General';
      return sum + (normalizeAggregateResponseTarget(editing.subheadingResponseTargets?.[key]).active ? 1 : 0);
    }, 0);

  const autoSaveKey = JSON.stringify({
    draftName: editing.draftName,
    description: editing.description,
    status: editing.status,
    responseDefaults: editing.responseDefaults,
    sectionResponseTarget: editing.sectionResponseTarget,
    subheadingResponseTargets: editing.subheadingResponseTargets,
    applyAll: editing.applyAll,
    draftFields: editing.draftFields,
  });

  useEffect(() => {
    if (firstAutoSave.current) {
      firstAutoSave.current = false;
      return undefined;
    }
    // Important: this autosave is local/session-only.
    // It must NOT write to Firestore or update the live Neo App standard.
    // Only the Publish button commits the edited standard.
    setSaveState({ status: 'saving', text: 'Saving draft locally…' });
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const now = new Date();
      const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const draftPayload = {
        draftName: editing.draftName,
        description: editing.description,
        status: editing.status,
        responseDefaults: editing.responseDefaults,
        sectionResponseTarget: editing.sectionResponseTarget,
        subheadingResponseTargets: editing.subheadingResponseTargets,
        applyAll: editing.applyAll,
        draftFields: editing.draftFields,
        lastSavedAt: now.toISOString(),
      };
      draftDbSet(editing.draftStorageKey, draftPayload)
        .then(() => setSaveState({ status: 'saved', text: `Draft saved ${time}` }))
        .catch((e) => {
          console.error(e);
          setSaveState({ status: 'error', text: 'Draft could not be saved' });
        });
    }, 450);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [autoSaveKey]);

  const setGroupPatch = (gi, patch) => setEditing(ed => {
    const currentGroups = ed.draftFields || [];
    const current = currentGroups[gi] || {};
    const nextGroups = currentGroups.map((g, i) => (i === gi ? { ...g, ...patch } : g));
    let nextTargets = { ...(ed.subheadingResponseTargets || {}) };

    if (Object.prototype.hasOwnProperty.call(patch || {}, 'subheading')) {
      const oldName = String(current.subheading || '').trim() || 'General';
      const newName = String(patch.subheading || '').trim() || 'General';
      if (oldName && oldName !== newName && nextTargets[oldName]) {
        const previousTarget = nextTargets[oldName];
        delete nextTargets[oldName];
        if (newName) nextTargets[newName] = previousTarget;
      }
    }

    return {
      ...ed,
      draftFields: nextGroups,
      subheadingResponseTargets: nextTargets,
    };
  });

  const addSubheading = () => {
    const name = (editing.newSub || '').trim();
    if (!name) return;
    const nextIndex = (editing.draftFields || []).length;
    setEditing(ed => ({ ...ed, draftFields: [...(ed.draftFields || []), { subheading: name, fields: [] }], newSub: '' }));
    // New sub-headings open immediately so the first duty can be added without another click.
    setCollapsedGroups(prev => ({ ...prev, [nextIndex]: false }));
  };

  const openNewDuty = (preferredGi = null) => {
    let gi = preferredGi;
    if (gi == null) gi = visibleGroups[0]?.gi;
    if (gi == null) gi = 0;
    if (!(editing.draftFields || []).length) {
      setEditing(ed => ({ ...ed, draftFields: [{ subheading: 'General', fields: [] }] }));
      setCollapsedGroups(prev => ({ ...prev, 0: false }));
      gi = 0;
    }
    setDutyModal({
      mode: 'new',
      gi,
      fi: null,
      form: { label: '', type: 'checkbox', subheading: groups[gi]?.subheading || 'General', instructions: '', ownerRoleId: '', ownerRoleName: '', responsibilityRouting: { mode: 'auto_assign', fallbackRoles: [] }, priority: 'normal', recurrence: { mode: 'daily', days: [] }, responseRule: { ...emptyResponseRule(), active: true } },
    });
  };

  const dutySelectionKey = (gi, fi) => `${gi}:${fi}`;
  const selectedDutyCount = Object.values(selectedDuties).filter(Boolean).length;
  const isGroupCollapsed = (gi) => collapsedGroups[gi] !== false;

  const toggleGroupCollapsed = (gi) => setCollapsedGroups(prev => ({ ...prev, [gi]: !isGroupCollapsed(gi) }));

  const setAllGroupsCollapsed = (collapsed) => {
    const next = {};
    visibleGroups.forEach(g => { next[g.gi] = collapsed; });
    setCollapsedGroups(next);
  };

  const toggleDutySelected = (gi, fi) => {
    const key = dutySelectionKey(gi, fi);
    setSelectedDuties(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const groupVisibleDutyKeys = (grp) => (grp.fields || []).map(f => dutySelectionKey(grp.gi, f.fi));
  const groupAllSelected = (grp) => {
    const keys = groupVisibleDutyKeys(grp);
    return keys.length > 0 && keys.every(key => !!selectedDuties[key]);
  };

  const toggleGroupSelected = (grp) => {
    const keys = groupVisibleDutyKeys(grp);
    const select = !groupAllSelected(grp);
    setSelectedDuties(prev => {
      const next = { ...prev };
      keys.forEach(key => { next[key] = select; });
      return next;
    });
  };

  const selectAllVisibleDuties = () => {
    const next = {};
    visibleGroups.forEach(grp => (grp.fields || []).forEach(f => { next[dutySelectionKey(grp.gi, f.fi)] = true; }));
    setSelectedDuties(next);
  };

  const clearDutySelection = () => setSelectedDuties({});

  const setSelectedDutyEnabledState = (enabled) => {
    if (!selectedDutyCount) return;
    setEditing(ed => ({
      ...ed,
      draftFields: (ed.draftFields || []).map((g, gi) => ({
        ...g,
        fields: (g.fields || []).map((f, fi) => selectedDuties[dutySelectionKey(gi, fi)] ? { ...f, disabled: !enabled } : f),
      })),
    }));
  };

  const requestDeleteSelectedDuties = () => {
    if (!selectedDutyCount) return;
    setConfirmAction({
      title: `Delete ${selectedDutyCount} selected dut${selectedDutyCount === 1 ? 'y' : 'ies'}?`,
      message: `This removes the selected dut${selectedDutyCount === 1 ? 'y' : 'ies'} from this draft. Nothing changes in the live standard until you Publish.`,
      okLabel: `Delete ${selectedDutyCount}`,
      danger: true,
      onConfirm: () => {
        setEditing(ed => ({
          ...ed,
          draftFields: (ed.draftFields || []).map((g, gi) => ({
            ...g,
            fields: (g.fields || []).filter((_, fi) => !selectedDuties[dutySelectionKey(gi, fi)]),
          })),
        }));
        setSelectedDuties({});
        setOpenMenu(null);
        setConfirmAction(null);
      },
    });
  };

  const openMorpheusDuty = (gi, fi=null) => {
    if(editing.applyAll || editing.storeId==='all') {toast.error('Select a single Neo site and turn off Include all stores for Morpheus duties.');return;}
    setCollapsedGroups(prev=>({...prev,[gi]:false}));
    setMorpheusModal({gi,fi,initial:fi==null?null:groups[gi]?.fields?.[fi]});setOpenMenu(null);
  };
  const saveMorpheusDuty = value => {
    const modal=morpheusModal;
    const dutyId=value.dutyId || `nm-duty-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
    setEditing(ed=>({...ed,draftFields:ed.draftFields.map((g,gi)=>gi!==modal.gi?g:{...g,fields:modal.fi==null?[...(g.fields || []),{...value,dutyId,disabled:false}]:(g.fields || []).map((f,fi)=>fi===modal.fi?{...f,...value,dutyId}:f)})}));
    setMorpheusModal(null);
  };
  const openEditDuty = (gi, fi) => {
    if(groups[gi]?.fields?.[fi]?.completionSource==='morpheus') {openMorpheusDuty(gi,fi);return;}
    const g = groups[gi] || {};
    const f = (g.fields || [])[fi] || {};
    setDutyModal({
      mode: 'edit', gi, fi,
      form: { label: f.label || '', type: f.type || 'checkbox', subheading: g.subheading || '', instructions: f.instructions || '', ownerRoleId: f.ownerRoleId || '', ownerRoleName: f.ownerRoleName || '', responsibilityRouting: f.responsibilityRouting || { mode: 'manager_decision', fallbackRoles: [] }, priority: f.priority || 'normal', recurrence: f.recurrence || { mode: 'daily', days: [] }, responseRule: normalizeResponseRule(f.responseRule) },
    });
    setOpenMenu(null);
  };

  const openAlarmDuty = (gi, fi) => {
    if(groups[gi]?.fields?.[fi]?.completionSource==='morpheus') {openMorpheusDuty(gi,fi);return;}
    const g = groups[gi] || {};
    const f = (g.fields || [])[fi] || {};
    setDutyModal({
      mode: 'edit', gi, fi,
      form: { label: f.label || '', type: f.type || 'checkbox', subheading: g.subheading || '', instructions: f.instructions || '', ownerRoleId: f.ownerRoleId || '', ownerRoleName: f.ownerRoleName || '', responsibilityRouting: f.responsibilityRouting || { mode: 'manager_decision', fallbackRoles: [] }, priority: f.priority || 'normal', recurrence: f.recurrence || { mode: 'daily', days: [] }, responseRule: { ...normalizeResponseRule(f.responseRule), active: true } },
    });
    setOpenMenu(null);
  };

  const saveDutyModal = () => {
    if (!dutyModal) return;
    const form = dutyModal.form || {};
    const targetSub = form.subheading || 'General';
    let targetGi = groups.findIndex(g => (g.subheading || '') === targetSub && !g.archived);
    setEditing(ed => {
      let draft = (ed.draftFields || []).map(g => ({ ...g, fields: [...(g.fields || [])] }));
      if (!draft.length) draft = [{ subheading: targetSub, fields: [] }];
      targetGi = draft.findIndex(g => (g.subheading || '') === targetSub && !g.archived);
      if (targetGi < 0) {
        draft.push({ subheading: targetSub, fields: [] });
        targetGi = draft.length - 1;
      }
      const payload = {
        label: form.label || 'Untitled duty',
        type: form.type || 'checkbox',
        instructions: form.instructions || '',
        ownerRoleId: form.ownerRoleId || '',
        ownerRoleName: form.ownerRoleName || '',
        responsibilityRouting: form.responsibilityRouting || { mode: 'manager_decision', fallbackRoles: [] },
        priority: form.priority || 'normal',
        recurrence: form.recurrence || { mode: 'daily', days: [] },
        responseRule: normalizeResponseRule(form.responseRule),
        disabled: false,
      };
      if (dutyModal.mode === 'edit') {
        const oldGi = dutyModal.gi;
        const oldFi = dutyModal.fi;
        if (oldGi === targetGi) {
          draft[oldGi].fields[oldFi] = { ...(draft[oldGi].fields[oldFi] || {}), ...payload };
        } else {
          const [existing] = draft[oldGi].fields.splice(oldFi, 1);
          draft[targetGi].fields.push({ ...(existing || {}), ...payload });
        }
      } else {
        draft[targetGi].fields.push(payload);
      }
      return { ...ed, draftFields: draft };
    });
    setDutyModal(null);
  };

  const requestDeleteDuty = (gi, fi) => {
    const dutyName = groups[gi]?.fields?.[fi]?.label || 'this duty';
    setConfirmAction({
      title: 'Delete duty?',
      message: `Delete “${dutyName}”? This removes the field from this standard after confirmation.`,
      okLabel: 'Delete duty',
      danger: true,
      onConfirm: () => {
        setEditing(ed => ({
          ...ed,
          draftFields: (ed.draftFields || []).map((g, i) => i === gi ? { ...g, fields: (g.fields || []).filter((_, j) => j !== fi) } : g),
        }));
        setSelectedDuties(prev => {
          const next = { ...prev };
          delete next[dutySelectionKey(gi, fi)];
          return next;
        });
        setOpenMenu(null);
        setConfirmAction(null);
      },
    });
  };

  const requestArchiveSubheading = (gi) => {
    const grp = groups[gi] || {};
    const count = (grp.fields || []).filter(f => !f.archived).length;
    setConfirmAction({
      title: count ? 'Archive subheading?' : 'Remove subheading?',
      message: count ? `Archive “${grp.subheading || 'this subheading'}” and its ${count} duties? They will no longer appear as active duties.` : `Remove “${grp.subheading || 'this subheading'}”?`,
      okLabel: count ? 'Archive subheading' : 'Remove subheading',
      danger: true,
      onConfirm: () => {
        setEditing(ed => {
          const nextTargets = { ...(ed.subheadingResponseTargets || {}) };
          const key = String(grp.subheading || '').trim() || 'General';
          delete nextTargets[key];
          return {
            ...ed,
            draftFields: (ed.draftFields || []).map((g, i) => i === gi ? { ...g, archived: !!count, fields: (g.fields || []).map(f => count ? { ...f, archived: true, disabled: true } : f) } : g).filter((g, i) => !(i === gi && !count)),
            subheadingResponseTargets: nextTargets,
          };
        });
        setSelectedDuties({});
        setConfirmAction(null);
      },
    });
  };

  const toggleDutyEnabled = (gi, fi) => updateDraftField(gi, fi, { disabled: !groups[gi]?.fields?.[fi]?.disabled });

  const handleAlarmClick = (gi, fi) => {
    if(groups[gi]?.fields?.[fi]?.completionSource==='morpheus') {openMorpheusDuty(gi,fi);return;}
    const rule = normalizeResponseRule(groups[gi]?.fields?.[fi]?.responseRule);
    if (rule.active) {
      updateDraftField(gi, fi, { responseRule: { ...rule, active: false } });
      return;
    }
    openAlarmDuty(gi, fi);
  };

  const patchResponseDefaults = (patch) => setEditing(ed => ({
    ...ed,
    responseDefaults: { ...(ed.responseDefaults || {}), ...patch },
  }));

  const patchSectionResponseTarget = (patch) => setEditing(ed => ({
    ...ed,
    sectionResponseTarget: {
      ...normalizeAggregateResponseTarget(ed.sectionResponseTarget),
      ...patch,
    },
  }));

  const patchSubheadingResponseTarget = (subheading, patch) => setEditing(ed => ({
    ...ed,
    subheadingResponseTargets: {
      ...(ed.subheadingResponseTargets || {}),
      [subheading]: {
        ...normalizeAggregateResponseTarget(ed.subheadingResponseTargets?.[subheading]),
        ...patch,
      },
    },
  }));

  const moveDuty = (fromGi, fromFi, toGi, toFi, position = 'before') => {
    if (fromGi == null || fromFi == null || toGi == null || toFi == null) return;

    setEditing(ed => {
      const draft = (ed.draftFields || []).map(g => ({ ...g, fields: [...(g.fields || [])] }));
      const sourceFields = draft[fromGi]?.fields;
      const targetFields = draft[toGi]?.fields;
      if (!sourceFields || !targetFields || !sourceFields[fromFi]) return ed;

      // Work out the intended insertion point against the original target
      // array, then compensate when moving forward inside the same sub-heading.
      let insertAt = toFi + (position === 'after' ? 1 : 0);
      const [item] = sourceFields.splice(fromFi, 1);
      if (!item) return ed;

      if (fromGi === toGi && fromFi < insertAt) insertAt -= 1;
      insertAt = Math.max(0, Math.min(targetFields.length, insertAt));

      // Dropping immediately back into the same slot is a no-op.
      if (fromGi === toGi && insertAt === fromFi) {
        sourceFields.splice(fromFi, 0, item);
        return ed;
      }

      targetFields.splice(insertAt, 0, item);
      return { ...ed, draftFields: draft };
    });

    setSelectedDuties({});
    setOpenMenu(null);
    setDragItem(null);
    setDutyDropTarget(null);
  };

  const moveDutyOneStep = (gi, fi, direction) => {
    const currentFields = (groups[gi]?.fields || [])
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => !field.archived);
    const visiblePosition = currentFields.findIndex(({ index }) => index === fi);
    if (visiblePosition < 0) return;

    const targetPosition = visiblePosition + direction;
    if (targetPosition < 0 || targetPosition >= currentFields.length) return;

    const targetFi = currentFields[targetPosition].index;
    moveDuty(gi, fi, gi, targetFi, direction < 0 ? 'before' : 'after');
  };

  const moveSubheading = (fromGi, toGi, position = 'before') => {
    if (fromGi == null || toGi == null || fromGi === toGi) return;
    const currentLength = (editing.draftFields || []).length;
    if (fromGi < 0 || toGi < 0 || fromGi >= currentLength || toGi >= currentLength) return;

    // Compute the destination against the original array, then compensate for
    // removing the dragged group first. This keeps before/after drops intuitive.
    let insertAt = toGi + (position === 'after' ? 1 : 0);
    if (fromGi < insertAt) insertAt -= 1;
    insertAt = Math.max(0, Math.min(currentLength - 1, insertAt));
    if (insertAt === fromGi) {
      setGroupDragItem(null);
      setGroupDropTarget(null);
      return;
    }

    setEditing(ed => {
      const draft = [...(ed.draftFields || [])];
      const [item] = draft.splice(fromGi, 1);
      if (!item) return ed;
      draft.splice(Math.max(0, Math.min(draft.length, insertAt)), 0, item);
      return { ...ed, draftFields: draft };
    });

    // Duty selection keys are index-based, so clear them after a group move.
    // Preserve each sub-heading's collapsed/expanded state as its array index
    // changes. Reordering must never open a collapsed sub-heading.
    setSelectedDuties({});
    setCollapsedGroups(prev => {
      const states = Array.from({ length: currentLength }, (_, index) => prev[index] !== false);
      const [movedState] = states.splice(fromGi, 1);
      states.splice(insertAt, 0, movedState);
      const next = {};
      states.forEach((isCollapsed, index) => { next[index] = isCollapsed; });
      return next;
    });
    setOpenMenu(null);
    setGroupDragItem(null);
    setGroupDropTarget(null);
    setDragItem(null);
    setDutyDropTarget(null);
  };

  const moveSubheadingOneStep = (gi, direction) => {
    const visibleIndexes = visibleGroups.map(g => g.gi);
    const currentPosition = visibleIndexes.indexOf(gi);
    if (currentPosition < 0) return;
    const targetPosition = currentPosition + direction;
    if (targetPosition < 0 || targetPosition >= visibleIndexes.length) return;
    const targetGi = visibleIndexes[targetPosition];
    moveSubheading(gi, targetGi, direction < 0 ? 'before' : 'after');
  };

  const confirmPublishNow = async () => {
    if (publishBusy) return;

    const groupedTargetsMissingDue = [];
    const currentSectionTarget = normalizeAggregateResponseTarget(editing.sectionResponseTarget);
    if (currentSectionTarget.active && !String(currentSectionTarget.dueTime || '').trim()) {
      groupedTargetsMissingDue.push(editing.draftName || 'Section');
    }
    visibleGroups.forEach((g) => {
      const key = String(g.subheading || '').trim() || 'General';
      const target = normalizeAggregateResponseTarget(editing.subheadingResponseTargets?.[key]);
      if (target.active && !String(target.dueTime || '').trim()) groupedTargetsMissingDue.push(key);
    });
    if (groupedTargetsMissingDue.length) {
      setSaveState({ status: 'error', text: 'Grouped reminder needs a due time' });
      toast.error(`Set a due time for: ${groupedTargetsMissingDue.join(', ')}`);
      setPublishConfirm(false);
      return;
    }

    setPublishBusy(true);
    setSaveState({ status: 'saving', text: 'Publishing…' });
    const ok = await handleSaveEdit({ close: false, publish: true });
    if (ok) {
      setSaveState({ status: 'saved', text: 'Published' });
      toast.success('Standard published');
      setPublishConfirm(false);
      setTimeout(() => setEditing(null), 650);
    } else {
      setSaveState({ status: 'error', text: 'Publish failed' });
      setPublishBusy(false);
    }
  };

  const renderStepContent = () => {
    if (editorStep === 'response') {
      const defaults = editing.responseDefaults || {};
      return (
        <section className="rounded-xl border border-white/10 p-5" style={{ background: 'rgba(15,28,48,0.82)' }}>
          <h2 className="text-xl font-semibold">Response Rules</h2>
          <p className="text-sm opacity-70 mt-1">Set the default escalation behaviour for this standard. Individual duties can still override these rules inside the duty modal.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <label className="text-sm opacity-90">Escalate to<input className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={defaults.escalateTo || ''} onChange={e => patchResponseDefaults({ escalateTo: e.target.value })} placeholder="Owner, manager email or phone" /></label>
            <label className="text-sm opacity-90">Escalation method<select className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={normalizeEscalationMethodValue(defaults.escalationMethod || 'email')} onChange={e => patchResponseDefaults({ escalationMethod: e.target.value })}><option value="email" style={darkSelectOptionStyle}>Email</option><option value="whatsapp" style={darkSelectOptionStyle}>WhatsApp</option><option value="both" style={darkSelectOptionStyle}>Email + WhatsApp</option></select></label>
            <label className="text-sm opacity-90">Reminder before due time<div className="flex mt-1"><input type="number" min="0" inputMode="numeric" className="w-full p-3 rounded-l border border-white/10" style={inputStyle} value={defaults.reminderBeforeMins || ''} onChange={e => patchResponseDefaults({ reminderBeforeMins: digitsOnly(e.target.value) })} /><span className="px-3 py-3 rounded-r border border-l-0 border-white/10 bg-white/5">min</span></div></label>
            <label className="text-sm opacity-90">Escalation eligible after due time<div className="flex mt-1"><input type="number" min="0" inputMode="numeric" className="w-full p-3 rounded-l border border-white/10" style={inputStyle} value={defaults.gracePeriodMins || ''} onChange={e => patchResponseDefaults({ gracePeriodMins: digitsOnly(e.target.value) })} /><span className="px-3 py-3 rounded-r border border-l-0 border-white/10 bg-white/5">min</span></div></label>
            <label className="text-sm opacity-90">Repeat reminder every<div className="flex mt-1"><input type="number" min="0" inputMode="numeric" className="w-full p-3 rounded-l border border-white/10" style={inputStyle} value={defaults.repeatEveryMins || ''} onChange={e => patchResponseDefaults({ repeatEveryMins: digitsOnly(e.target.value) })} /><span className="px-3 py-3 rounded-r border border-l-0 border-white/10 bg-white/5">min</span></div></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm opacity-90">Quiet hours start<input type="time" className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={defaults.quietHoursStart || ''} onChange={e => patchResponseDefaults({ quietHoursStart: e.target.value })} /></label>
              <label className="text-sm opacity-90">Quiet hours end<input type="time" className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={defaults.quietHoursEnd || ''} onChange={e => patchResponseDefaults({ quietHoursEnd: e.target.value })} /></label>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-cyan-400/20 p-4" style={{ background: 'rgba(34,211,238,0.045)' }}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-cyan-200">Reminder target level</h3>
                <p className="text-sm opacity-70 mt-1 max-w-3xl">Neo can now protect a single field, a whole sub-heading, or the entire section. Group targets complete from the existing checklist evidence — no second checklist is created.</p>
              </div>
              <span className="text-xs px-3 py-1 rounded-full border border-cyan-400/25 text-cyan-200 bg-cyan-400/10">{activeAggregateTargets} grouped target{activeAggregateTargets === 1 ? '' : 's'} active</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
              <div className="rounded-lg border border-white/10 p-4" style={{ background: 'rgba(0,0,0,0.14)' }}>
                <div className="text-xs uppercase tracking-[0.16em] text-sky-300">Field</div>
                <div className="font-semibold mt-1">Individual duty</div>
                <div className="text-xs opacity-65 mt-1">Completion: that field is answered/completed. Configure these from each duty's Alarm button.</div>
                <div className="mt-3 text-sm"><span className="font-semibold">{activeAlarms}</span> active field alarm{activeAlarms === 1 ? '' : 's'}</div>
              </div>

              <div className="lg:col-span-2">
                <AggregateResponseTargetCard
                  title={editing.draftName || 'Entire section'}
                  targetType="section"
                  target={sectionAggregateTarget}
                  defaults={defaults}
                  roles={roles}
                  completionText="Completes only when the whole section is successfully submitted."
                  onPatch={patchSectionResponseTarget}
                />
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs uppercase tracking-[0.16em] text-cyan-300 mb-2">Sub-heading targets</div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                {visibleGroups.map((g) => {
                  const key = String(g.subheading || '').trim() || 'General';
                  const target = normalizeAggregateResponseTarget(editing.subheadingResponseTargets?.[key]);
                  return (
                    <AggregateResponseTargetCard
                      key={`aggregate-${g.gi}-${key || 'general'}`}
                      title={key || 'General'}
                      targetType="subheading"
                      target={target}
                      defaults={defaults}
                      roles={roles}
                      completionText={`Completes when all ${(g.fields || []).filter(f => !f.disabled).length} active field${(g.fields || []).filter(f => !f.disabled).length === 1 ? '' : 's'} under this sub-heading are complete and saved.`}
                      onPatch={(patch) => patchSubheadingResponseTarget(key, patch)}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-5"><MetricBox label="Field alarms" value={activeAlarms} /><MetricBox label="Grouped targets" value={activeAggregateTargets} /><MetricBox label="Duties without field alarm" value={Math.max(0, activeDuties - activeAlarms)} /><MetricBox label="Field alarm coverage" value={`${coverage}%`} ring /></div>
        </section>
      );
    }
    if (editorStep === 'review') {
      const subheadingRows = visibleGroups.map(g => ({ name: g.subheading || 'General', total: (g.fields || []).filter(f => (f.label || '').trim()).length, active: (g.fields || []).filter(f => (f.label || '').trim() && !f.disabled).length, alarms: (g.fields || []).filter(f => !f.disabled && f?.responseRule?.active).length }));
      const inactiveCount = allDuties.filter(f => f.disabled).length;
      const archivedCount = (groups || []).reduce((sum, g) => sum + (g.archived ? 1 : 0) + ((g.fields || []).filter(f => f.archived).length), 0);
      return (
        <section className="rounded-xl border border-white/10 p-5" style={{ background: 'rgba(15,28,48,0.82)' }}>
          <h2 className="text-xl font-semibold">Review & Publish</h2>
          <p className="text-sm opacity-70 mt-1">Changes are kept as a draft until you publish. Publish is the only action that updates the live Neo App standard.</p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-5"><MetricBox label="Subheadings" value={subheadingRows.length} /><MetricBox label="Total duties" value={totalDuties} /><MetricBox label="Active duties" value={activeDuties} /><MetricBox label="Inactive duties" value={inactiveCount} /><MetricBox label="Alarm coverage" value={`${coverage}%`} ring /></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
            <div className="rounded-lg border border-white/10 p-4"><h3 className="font-semibold mb-3">Duties by subheading</h3><div className="space-y-2">{subheadingRows.map(row => <div key={row.name} className="flex items-center justify-between gap-3 rounded border border-white/10 p-3"><span>{row.name}</span><span className="text-sm opacity-80">{row.active}/{row.total} active • {row.alarms} alarms</span></div>)}</div></div>
            <div className="rounded-lg border border-white/10 p-4"><h3 className="font-semibold mb-3">Draft summary</h3><div className="space-y-2 text-sm opacity-80"><div>Store: <span className="font-semibold text-white">{editing.storeName || 'Selected store'}</span></div><div>Include all stores: <span className="font-semibold text-white">{editing.applyAll ? 'Yes' : 'No'}</span></div><div>Archived items in draft: <span className="font-semibold text-white">{archivedCount}</span></div><div>Publish status: <span className="font-semibold text-amber-300">Draft only — not published</span></div></div></div>
          </div>
        </section>
      );
    }
    return (
      <section className="rounded-xl border border-white/10 p-3 md:p-4" style={{ background: 'rgba(15,28,48,0.82)' }}>
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-semibold">Duties & Checks</h2>
              <span className="text-xs px-2 py-1 rounded-full border border-cyan-400/20 text-cyan-200" style={{ background: 'rgba(34,211,238,0.08)' }}>{visibleGroups.length} sub-heading{visibleGroups.length === 1 ? '' : 's'}</span>
              <span className="text-xs px-2 py-1 rounded-full border border-white/10 opacity-80">{activeDuties}/{totalDuties} active</span>
            </div>
            <p className="text-sm opacity-70 mt-1">Build the standard by area. Drag sub-headings and duties into the order staff should see them. On touch devices use the sub-heading arrows and each duty's menu to move it up or down. Sub-headings stay collapsed until you choose to open them.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="px-3 py-2 rounded-lg border border-white/10 text-sm hover:bg-white/5" onClick={() => setAllGroupsCollapsed(false)}>Expand all</button>
            <button className="px-3 py-2 rounded-lg border border-white/10 text-sm hover:bg-white/5" onClick={() => setAllGroupsCollapsed(true)}>Collapse all</button>
          </div>
        </div>

        <div className="rounded-xl border border-white/10 p-3 mb-4" style={{ background: 'rgba(255,255,255,0.025)' }}>
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.18em] text-cyan-300 mb-1">New sub-heading</div>
              <input
                className="w-full p-2.5 rounded-lg border border-white/10"
                style={inputStyle}
                placeholder="e.g. Cashier, Storeroom, Lounge, Toilets"
                value={editing.newSub || ''}
                onChange={e => setEditing(ed => ({ ...ed, newSub: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSubheading(); } }}
              />
            </div>
            <button className="px-4 py-2.5 rounded-lg font-semibold text-white md:self-end" style={accentBtn} onClick={addSubheading}>+ Add Sub-heading</button>
          </div>
        </div>

        <div className="rounded-xl border border-white/10 p-3 mb-4" style={{ background: selectedDutyCount ? 'rgba(34,211,238,0.07)' : 'rgba(255,255,255,0.02)' }}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <div className="font-semibold">Bulk actions</div>
              <div className="text-xs opacity-65 mt-0.5">{selectedDutyCount ? `${selectedDutyCount} dut${selectedDutyCount === 1 ? 'y' : 'ies'} selected` : 'Select duties below to edit several at once.'}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="px-3 py-2 rounded-lg border border-white/10 text-sm hover:bg-white/5" onClick={selectAllVisibleDuties} disabled={!totalDuties}>Select all</button>
              <button className="px-3 py-2 rounded-lg border border-white/10 text-sm hover:bg-white/5 disabled:opacity-40" onClick={clearDutySelection} disabled={!selectedDutyCount}>Deselect all</button>
              <button className="px-3 py-2 rounded-lg border border-emerald-400/30 text-emerald-200 text-sm disabled:opacity-40" onClick={() => setSelectedDutyEnabledState(true)} disabled={!selectedDutyCount}>Activate</button>
              <button className="px-3 py-2 rounded-lg border border-amber-400/30 text-amber-200 text-sm disabled:opacity-40" onClick={() => setSelectedDutyEnabledState(false)} disabled={!selectedDutyCount}>Deactivate</button>
              <button className="px-3 py-2 rounded-lg border border-rose-400/35 text-rose-200 text-sm disabled:opacity-40" onClick={requestDeleteSelectedDuties} disabled={!selectedDutyCount}>Delete selected</button>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {visibleGroups.map((grp, visibleIndex) => {
            const collapsed = isGroupCollapsed(grp.gi);
            const groupFields = grp.fields || [];
            const activeCount = groupFields.filter(f => !f.disabled).length;
            const alarmCount = groupFields.filter(f => !f.disabled && normalizeResponseRule(f.responseRule).active).length;
            const allSelected = groupAllSelected(grp);
            const isDraggingGroup = groupDragItem?.gi === grp.gi;
            const isDropTarget = groupDropTarget?.gi === grp.gi && groupDragItem?.gi !== grp.gi;
            const dropPosition = isDropTarget ? groupDropTarget.position : null;
            return (
              <div
                key={`${grp.subheading}-${grp.gi}`}
                className={`relative rounded-xl border overflow-visible transition-all ${isDropTarget ? 'border-cyan-400/70' : 'border-white/10'} ${isDraggingGroup ? 'opacity-50 scale-[0.995]' : ''}`}
                style={{
                  background: 'rgba(0,0,0,0.14)',
                  boxShadow: dropPosition === 'before'
                    ? 'inset 0 3px 0 rgba(34,211,238,0.9)'
                    : dropPosition === 'after'
                      ? 'inset 0 -3px 0 rgba(34,211,238,0.9)'
                      : undefined,
                }}
                onDragOver={(e) => {
                  if (!groupDragItem || groupDragItem.gi === grp.gi) return;
                  e.preventDefault();
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const position = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                  if (groupDropTarget?.gi !== grp.gi || groupDropTarget?.position !== position) {
                    setGroupDropTarget({ gi: grp.gi, position });
                  }
                }}
                onDrop={(e) => {
                  if (!groupDragItem || groupDragItem.gi === grp.gi) return;
                  e.preventDefault();
                  e.stopPropagation();
                  moveSubheading(groupDragItem.gi, grp.gi, groupDropTarget?.position || 'before');
                }}
              >
                <div className="p-3 md:p-4" style={{ background: collapsed ? 'rgba(255,255,255,0.035)' : 'rgba(34,211,238,0.055)' }}>
                  <div className="flex items-start gap-2 md:gap-3">
                    <button
                      type="button"
                      draggable
                      className="mt-0.5 w-9 h-9 shrink-0 rounded-lg border border-cyan-400/20 text-cyan-200/80 flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-cyan-400/10 select-none"
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', String(grp.gi));
                        setGroupDragItem({ gi: grp.gi });
                        setGroupDropTarget(null);
                        setDragItem(null);
                        setDutyDropTarget(null);
                      }}
                      onDragEnd={() => { setGroupDragItem(null); setGroupDropTarget(null); }}
                      onClick={(e) => e.preventDefault()}
                      aria-label={`Drag ${grp.subheading || 'sub-heading'} to reorder`}
                      title="Drag sub-heading to reorder"
                    >
                      ⠿
                    </button>
                    <button
                      type="button"
                      className="mt-0.5 w-9 h-9 shrink-0 rounded-lg border border-white/10 flex items-center justify-center hover:bg-white/10"
                      onClick={() => toggleGroupCollapsed(grp.gi)}
                      aria-label={collapsed ? 'Expand sub-heading' : 'Collapse sub-heading'}
                      title={collapsed ? 'Expand sub-heading' : 'Collapse sub-heading'}
                    >
                      <span className={`transition-transform ${collapsed ? '' : 'rotate-90'}`}>›</span>
                    </button>
                    <div className="min-w-0 flex-1">
                      <input
                        className="w-full bg-transparent text-base md:text-lg font-semibold outline-none border-b border-transparent focus:border-cyan-400/40 pb-1"
                        value={grp.subheading}
                        onChange={e => setGroupPatch(grp.gi, { subheading: e.target.value })}
                        onClick={e => e.stopPropagation()}
                        aria-label="Sub-heading name"
                      />
                      <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs opacity-75">
                        <span>{groupFields.length} dut{groupFields.length === 1 ? 'y' : 'ies'}</span>
                        <span>•</span>
                        <span>{activeCount} active</span>
                        <span>•</span>
                        <span>{alarmCount} alarm{alarmCount === 1 ? '' : 's'}</span>
                        {allSelected && <span className="px-2 py-0.5 rounded-full text-cyan-200 border border-cyan-400/20" style={{ background: 'rgba(34,211,238,0.08)' }}>All selected</span>}
                      </div>
                    </div>
                    <div className="hidden lg:flex items-center gap-2 shrink-0">
                      <button className="px-3 py-2 rounded-lg border border-cyan-400/30 text-cyan-200 text-sm" onClick={() => { setCollapsedGroups(prev => ({ ...prev, [grp.gi]: false })); openNewDuty(grp.gi); }}>+ New Duty</button>
                      <button className="px-3 py-2 rounded-lg border border-cyan-400/30 text-cyan-200 text-sm" onClick={() => openMorpheusDuty(grp.gi)}>+ Morpheus Duty</button>
                      <button className="px-3 py-2 rounded-lg border border-white/10 text-sm" onClick={() => toggleGroupSelected(grp)}>{allSelected ? 'Deselect all' : 'Select all'}</button>
                      <button className="px-3 py-2 rounded-lg border border-rose-400/25 text-rose-200/90 text-sm" title="Archive sub-heading" onClick={() => requestArchiveSubheading(grp.gi)}>Archive</button>
                    </div>
                  </div>
                  <div className="flex lg:hidden flex-wrap gap-2 mt-3 pl-[88px]">
                    <button className="px-3 py-2 rounded-lg border border-cyan-400/30 text-cyan-200 text-sm" onClick={() => { setCollapsedGroups(prev => ({ ...prev, [grp.gi]: false })); openNewDuty(grp.gi); }}>+ New Duty</button>
                      <button className="px-3 py-2 rounded-lg border border-cyan-400/30 text-cyan-200 text-sm" onClick={() => openMorpheusDuty(grp.gi)}>+ Morpheus Duty</button>
                    <button className="px-3 py-2 rounded-lg border border-white/10 text-sm" onClick={() => toggleGroupSelected(grp)}>{allSelected ? 'Deselect all' : 'Select all'}</button>
                    <button className="px-3 py-2 rounded-lg border border-rose-400/25 text-rose-200/90 text-sm" title="Archive sub-heading" onClick={() => requestArchiveSubheading(grp.gi)}>Archive</button>
                  </div>
                  <div className="flex xl:hidden items-center gap-2 mt-3 pl-[88px] text-xs">
                    <span className="opacity-60 mr-1">Order</span>
                    <button
                      type="button"
                      className="w-9 h-8 rounded-lg border border-white/10 disabled:opacity-30 hover:bg-white/10"
                      disabled={visibleIndex === 0}
                      onClick={() => moveSubheadingOneStep(grp.gi, -1)}
                      aria-label={`Move ${grp.subheading || 'sub-heading'} up`}
                      title="Move sub-heading up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="w-9 h-8 rounded-lg border border-white/10 disabled:opacity-30 hover:bg-white/10"
                      disabled={visibleIndex === visibleGroups.length - 1}
                      onClick={() => moveSubheadingOneStep(grp.gi, 1)}
                      aria-label={`Move ${grp.subheading || 'sub-heading'} down`}
                      title="Move sub-heading down"
                    >
                      ↓
                    </button>
                    <span className="opacity-50">{visibleIndex + 1} of {visibleGroups.length}</span>
                  </div>
                </div>

                {!collapsed && (
                  <div className="divide-y divide-white/10">
                    {groupFields.length === 0 && (
                      <div className="px-4 py-8 text-center">
                        <div className="font-semibold">No duties in this sub-heading yet.</div>
                        <div className="text-sm opacity-60 mt-1">Add the first duty without leaving this area.</div>
                        <button className="mt-3 px-4 py-2 rounded-lg border border-cyan-400/30 text-cyan-200" onClick={() => openNewDuty(grp.gi)}>+ New Duty</button>
                      <button className="mt-3 px-4 py-2 rounded-lg border border-cyan-400/30 text-cyan-200" onClick={() => openMorpheusDuty(grp.gi)}>+ Morpheus Duty</button>
                      </div>
                    )}
                    {groupFields.map((f, dutyVisibleIndex) => {
                      const rule = normalizeResponseRule(f.responseRule);
                      const menuKey = `${grp.gi}-${f.fi}`;
                      const selected = !!selectedDuties[dutySelectionKey(grp.gi, f.fi)];
                      const isDraggingDuty = dragItem?.gi === grp.gi && dragItem?.fi === f.fi;
                      const isDutyDropTarget = dutyDropTarget?.gi === grp.gi && dutyDropTarget?.fi === f.fi && !isDraggingDuty;
                      const dutyDropPosition = isDutyDropTarget ? dutyDropTarget.position : null;
                      return (
                        <div
                          key={`${f.label}-${f.fi}`}
                          onDragOver={(e) => {
                            if (!dragItem || isDraggingDuty) return;
                            e.preventDefault();
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            const position = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                            if (
                              dutyDropTarget?.gi !== grp.gi ||
                              dutyDropTarget?.fi !== f.fi ||
                              dutyDropTarget?.position !== position
                            ) {
                              setDutyDropTarget({ gi: grp.gi, fi: f.fi, position });
                            }
                          }}
                          onDrop={(e) => {
                            if (!dragItem || isDraggingDuty) return;
                            e.preventDefault();
                            e.stopPropagation();
                            moveDuty(dragItem.gi, dragItem.fi, grp.gi, f.fi, dutyDropTarget?.position || 'before');
                          }}
                          className={`relative grid grid-cols-[24px_32px_minmax(0,1fr)_38px] md:grid-cols-[24px_32px_minmax(0,1fr)_130px_110px_38px] items-center gap-2 md:gap-3 px-3 py-3 transition-colors ${selected ? 'bg-cyan-400/10' : 'hover:bg-white/5'} ${isDraggingDuty ? 'opacity-50' : ''}`}
                          style={{
                            ...(f.disabled ? { opacity: 0.55 } : {}),
                            boxShadow: dutyDropPosition === 'before'
                              ? 'inset 0 2px 0 rgba(34,211,238,0.95)'
                              : dutyDropPosition === 'after'
                                ? 'inset 0 -2px 0 rgba(34,211,238,0.95)'
                                : undefined,
                          }}
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-cyan-400 cursor-pointer"
                            checked={selected}
                            onChange={() => toggleDutySelected(grp.gi, f.fi)}
                            onClick={e => e.stopPropagation()}
                            aria-label={`Select ${f.label || 'duty'}`}
                          />
                          <button
                            type="button"
                            draggable
                            className="w-8 h-8 rounded-lg border border-transparent hover:border-cyan-400/20 hover:bg-cyan-400/10 text-cyan-100/60 hover:text-cyan-100 flex items-center justify-center cursor-grab active:cursor-grabbing select-none"
                            title="Drag duty to reorder"
                            aria-label={`Drag ${f.label || 'duty'} to reorder`}
                            onDragStart={(e) => {
                              e.stopPropagation();
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', `${grp.gi}:${f.fi}`);
                              setDragItem({ gi: grp.gi, fi: f.fi });
                              setDutyDropTarget(null);
                              setGroupDragItem(null);
                              setGroupDropTarget(null);
                            }}
                            onDragEnd={() => {
                              setDragItem(null);
                              setDutyDropTarget(null);
                            }}
                            onClick={(e) => e.preventDefault()}
                          >
                            ⠿
                          </button>
                          <div className="min-w-0">
                            <button className="font-medium truncate text-left w-full" onClick={() => openEditDuty(grp.gi, f.fi)}>{f.label || 'Untitled duty'}</button>
                            <div className="md:hidden flex flex-wrap items-center gap-2 mt-1.5 text-[11px]">
                              <span className="px-2 py-0.5 rounded border border-white/10 capitalize opacity-75">{f.completionSource==='morpheus'?'Morpheus duty':f.type || 'checkbox'}</span>
                              <button type="button" className={`${rule.active ? 'text-emerald-300' : 'text-amber-300'}`} onClick={(e) => { e.stopPropagation(); handleAlarmClick(grp.gi, f.fi); }}>● {rule.active ? 'Alarm on' : 'Alarm off'}</button>
                              <button type="button" className={f.disabled ? 'text-white/50' : 'text-cyan-300'} onClick={(e) => { e.stopPropagation(); toggleDutyEnabled(grp.gi, f.fi); }}>{f.disabled ? 'Inactive' : 'Active'}</button>
                            </div>
                          </div>
                          <select className="hidden md:block text-xs px-2 py-1 rounded bg-white/10 capitalize border border-white/10" disabled={f.completionSource==='morpheus'} value={f.type || 'checkbox'} onChange={(e) => { e.stopPropagation(); updateDraftField(grp.gi, f.fi, { type: e.target.value }); }} onClick={(e) => e.stopPropagation()}>
                            {f.completionSource==='morpheus'&&<option value="morpheus_count" style={darkSelectOptionStyle}>Morpheus duty</option>}
                            {FIELD_TYPES.map(t => <option key={t} value={t} style={darkSelectOptionStyle}>{t}</option>)}
                          </select>
                          <button type="button" className={`hidden md:block text-xs text-left ${rule.active ? 'text-emerald-300' : 'text-amber-300'}`} onClick={(e) => { e.stopPropagation(); handleAlarmClick(grp.gi, f.fi); }}>● {rule.active ? 'Alarm on' : 'Alarm off'}</button>
                          <button className="opacity-70 hover:opacity-100 text-xl" onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === menuKey ? null : menuKey); }}>⋮</button>
                          {openMenu === menuKey && (
                            <div className="absolute right-3 top-11 z-20 w-44 rounded-lg border border-white/10 shadow-xl overflow-hidden" style={{ background: '#0b1627' }} onClick={(e) => e.stopPropagation()}>
                              <button
                                className="w-full text-left px-3 py-2 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
                                disabled={dutyVisibleIndex === 0}
                                onClick={() => moveDutyOneStep(grp.gi, f.fi, -1)}
                              >
                                ↑ Move duty up
                              </button>
                              <button
                                className="w-full text-left px-3 py-2 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent border-b border-white/10"
                                disabled={dutyVisibleIndex === groupFields.length - 1}
                                onClick={() => moveDutyOneStep(grp.gi, f.fi, 1)}
                              >
                                ↓ Move duty down
                              </button>
                              <button className="w-full text-left px-3 py-2 hover:bg-white/10" onClick={() => openEditDuty(grp.gi, f.fi)}>Edit duty</button>
                              <button className="w-full text-left px-3 py-2 hover:bg-white/10" onClick={() => { toggleDutyEnabled(grp.gi, f.fi); setOpenMenu(null); }}>{f.disabled ? 'Activate duty' : 'Deactivate duty'}</button>
                              <button className="w-full text-left px-3 py-2 hover:bg-white/10 text-rose-300" onClick={() => requestDeleteDuty(grp.gi, f.fi)}>Delete duty</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {groupFields.length > 0 && (
                      <div className="p-3 flex flex-wrap gap-2 justify-center md:justify-start">
                        <button className="w-full md:w-auto px-4 py-2 rounded-lg border border-dashed border-cyan-400/30 text-cyan-200 hover:bg-cyan-400/5" onClick={() => openNewDuty(grp.gi)}>+ New Duty in {grp.subheading || 'General'}</button>
                      <button className="w-full md:w-auto px-4 py-2 rounded-lg border border-dashed border-cyan-400/30 text-cyan-200 hover:bg-cyan-400/5" onClick={() => openMorpheusDuty(grp.gi)}>+ Morpheus Duty</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!visibleGroups.length && (
          <div className="rounded-xl border border-dashed border-white/15 py-12 px-5 text-center">
            <div className="text-lg font-semibold">No active sub-headings yet.</div>
            <div className="text-sm opacity-60 mt-1">Create a sub-heading above, then add duties inside it.</div>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="fixed inset-0 z-[2147483647] overflow-hidden text-white isolate neo-fullscreen-editor" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, height: '100dvh', background: 'linear-gradient(135deg, #07111f 0%, #0b1627 55%, #07101c 100%)' }}>
      <style>{`
        body.neo-standard-editor-open .neo-admin-topbar { display: none !important; }
        .neo-fullscreen-editor select, .neo-duty-modal select { background-color: #0b1627 !important; color: #fff !important; color-scheme: dark; }
        .neo-fullscreen-editor select option, .neo-duty-modal select option { background: #0b1627 !important; color: #fff !important; }
        .neo-fullscreen-editor input[type="time"]::-webkit-calendar-picker-indicator, .neo-duty-modal input[type="time"]::-webkit-calendar-picker-indicator { filter: invert(1) brightness(2); opacity: 1; cursor: pointer; }
        .neo-fullscreen-editor input[type="time"], .neo-duty-modal input[type="time"] { color-scheme: dark; }
      `}</style><div className="h-screen flex flex-col" style={{ height: '100dvh', minHeight: 0 }}>
        <div className="shrink-0 flex items-start justify-between gap-4 px-6 py-3 border-b border-white/10" style={{ background: 'rgba(7,17,31,0.98)' }}>
          <div><h1 className="text-2xl md:text-3xl font-bold">Edit Standard / Section</h1><p className="text-sm opacity-75 mt-1">Design the duties, checks and response rules for this standard.</p></div>
          <div className="hidden lg:flex items-center gap-3"><MetricBox label="Total duties" value={totalDuties} /><MetricBox label="Active alarms" value={activeAlarms} /><MetricBox label="Alarm coverage" value={`${coverage}%`} ring /></div>
          <button className="text-2xl px-3 py-1 rounded hover:bg-white/10" onClick={() => setEditing(null)}>×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 md:p-6" style={{ paddingBottom: 150, minHeight: 0 }}>
          <div className="grid grid-cols-1 gap-4">
            <aside className="rounded-xl border border-white/10 p-4 h-fit" style={{ background: 'rgba(15,28,48,0.85)' }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Step number="1" title="Build Standard" text="Add duties and checks" active={editorStep === 'build'} onClick={() => setEditorStep('build')} />
                <Step number="2" title="Response Rules" text="Escalation defaults" active={editorStep === 'response'} onClick={() => setEditorStep('response')} />
                <Step number="3" title="Review & Publish" text="Draft summary" active={editorStep === 'review'} onClick={() => setEditorStep('review')} />
              </div>
              <div className="rounded-lg border border-sky-400/20 p-3 text-sm mt-3" style={{ background: 'rgba(14,165,233,0.08)' }}><div className="font-semibold text-cyan-300">💡 Need help?</div><p className="opacity-75 leading-relaxed mt-1">Best practice: keep duties simple, observable and measurable. Each duty must have a clear owner and due time.</p></div>
            </aside>
            <main className="space-y-4">
              <div className="rounded-xl border border-white/10 p-4" style={{ background: 'rgba(15,28,48,0.82)' }}>
                <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr_190px] gap-4 items-end">
                  <label className="text-sm opacity-90">Store<select className="w-full mt-1 p-2 rounded border border-cyan-500/40" style={inputStyle} value={editing.storeId || ''} disabled><option>{editing.storeName || 'Selected store'}</option></select></label>
                  <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={!!editing.applyAll} onChange={e => setEditing(ed => ({ ...ed, applyAll: e.target.checked }))} /><span>Include all stores</span></label>
                  <label className="text-sm opacity-90">Status<select className="w-full mt-1 p-2 rounded border border-white/10" style={inputStyle} value={editing.status || 'active'} onChange={e => setEditing(ed => ({ ...ed, status: e.target.value }))}><option value="active">● Active</option><option value="draft">○ Draft</option></select></label>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-4 mt-4">
                  <label className="text-sm opacity-90">Section / Standard Name *<input className="w-full mt-1 p-3 rounded border border-white/10 text-base" style={inputStyle} value={editing.draftName} onChange={e => setEditing(ed => ({ ...ed, draftName: e.target.value }))} /></label>
                  <label className="text-sm opacity-90">Description (optional)<input className="w-full mt-1 p-3 rounded border border-white/10 text-base" style={inputStyle} value={editing.description || ''} onChange={e => setEditing(ed => ({ ...ed, description: e.target.value }))} placeholder="Daily opening duties to prepare store for trading." /></label>
                </div>
              </div>
              {renderStepContent()}
            </main>
          </div>
        </div>
        <div className="fixed bottom-0 left-0 right-0 z-40 px-6 py-4 border-t border-white/10 flex items-center justify-between gap-3" style={{ background: 'rgba(7,17,31,0.98)' }}>
          <button className="px-4 py-2 rounded border border-white/20" onClick={() => setEditing(null)}>Close</button>
          <div className="flex items-center gap-4"><span className={`text-sm ${saveState.status === 'error' ? 'text-rose-300' : saveState.status === 'saving' ? 'text-amber-300' : 'text-emerald-300'}`}>{saveState.text}</span><button className="px-5 py-2 rounded text-white font-semibold" style={accentBtn} onClick={() => setPublishConfirm(true)}>Publish</button></div>
        </div>
      </div>
      {morpheusModal && <NeoMorpheusDutyDialog orgId={orgId} storeId={editing.storeId} standardName={editing.draftName} subheading={groups[morpheusModal.gi]?.subheading} initialDuty={morpheusModal.initial} onSave={saveMorpheusDuty} onCancel={()=>setMorpheusModal(null)} />}
      {dutyModal && <DutyModal dutyModal={dutyModal} setDutyModal={setDutyModal} groups={visibleGroups} roles={roles} onSave={saveDutyModal} />}
      {confirmAction && <LocalConfirm action={confirmAction} onCancel={() => setConfirmAction(null)} />}
      {publishConfirm && <LocalConfirm action={{
        title: publishBusy ? 'Publishing standard…' : 'Publish standard?',
        message: publishBusy ? 'Please wait while Neo publishes this draft to the live standard.' : 'Publish this draft to the live Neo App standard? Staff will see the latest published duties and alarm rules after this is confirmed.',
        okLabel: publishBusy ? 'Publishing…' : 'Publish now',
        danger: false,
        busy: publishBusy,
        onConfirm: confirmPublishNow,
      }} onCancel={() => { if (!publishBusy) setPublishConfirm(false); }} />}
    </div>
  );
}

function AggregateResponseTargetCard({ title, targetType, target, defaults = {}, roles = [], completionText, onPatch }) {
  const t = normalizeAggregateResponseTarget(target);
  const reminderValue = t.remindBeforeMins === '' ? String(defaults.reminderBeforeMins || '') : String(t.remindBeforeMins);
  const graceValue = t.escalateAfterMins === '' ? String(defaults.gracePeriodMins || defaults.escalateAfterMins || '') : String(t.escalateAfterMins);
  const repeatValue = t.repeatEveryMins === '' ? String(defaults.repeatEveryMins || '') : String(t.repeatEveryMins);
  const targetLabel = targetType === 'section' ? 'Section' : 'Sub-heading';

  return (
    <div className={`rounded-lg border p-4 ${t.active ? 'border-emerald-400/30' : 'border-white/10'}`} style={{ background: t.active ? 'rgba(16,185,129,0.055)' : 'rgba(0,0,0,0.14)' }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.14em] text-cyan-300">{targetLabel}</div>
          <div className="font-semibold mt-1 truncate">{title}</div>
          <div className="text-xs opacity-65 mt-1">{completionText}</div>
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold whitespace-nowrap">
          <span className={t.active ? 'text-emerald-300' : 'opacity-60'}>{t.active ? 'Alarm on' : 'Alarm off'}</span>
          <input type="checkbox" checked={!!t.active} onChange={(e) => onPatch({ active: e.target.checked })} />
        </label>
      </div>

      {t.active ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-sm">
          <label>Due time *
            <input type="time" className="w-full mt-1 p-2 rounded border border-white/10" style={inputStyle} value={t.dueTime || ''} onChange={(e) => onPatch({ dueTime: e.target.value })} />
          </label>
          <label>Accountable role
            <select className="w-full mt-1 p-2 rounded border border-white/10" style={inputStyle} value={t.ownerRoleId || ''} onChange={(e) => { const role = roles.find(r => r.id === e.target.value); onPatch({ ownerRoleId: e.target.value, ownerRoleName: role?.name || '' }); }}>
              <option value="" style={darkSelectOptionStyle}>Any scheduled staff</option>
              {roles.map(role => <option key={role.id} value={role.id} style={darkSelectOptionStyle}>{role.name || 'Unnamed role'}</option>)}
            </select>
          </label>
          <label>Reminder before
            <div className="flex mt-1"><input type="number" min="0" inputMode="numeric" className="w-full p-2 rounded-l border border-white/10" style={inputStyle} value={reminderValue} onChange={(e) => onPatch({ remindBeforeMins: digitsOnly(e.target.value) })} /><span className="px-3 py-2 rounded-r border border-l-0 border-white/10 bg-white/5">min</span></div>
          </label>
          <label>Grace period
            <div className="flex mt-1"><input type="number" min="0" inputMode="numeric" className="w-full p-2 rounded-l border border-white/10" style={inputStyle} value={graceValue} onChange={(e) => {
              const nextGrace = digitsOnly(e.target.value);
              const n = Number(nextGrace || 0);
              const r = Number(repeatValue || 0);
              onPatch({
                escalateAfterMins: nextGrace,
                ...(n > 1 && r > 0 ? { repeatEveryMins: String(Math.min(r, n - 1)) } : n <= 1 ? { repeatEveryMins: '' } : {}),
              });
            }} /><span className="px-3 py-2 rounded-r border border-l-0 border-white/10 bg-white/5">min</span></div>
          </label>
          <label>Repeat during grace
            <div className="flex mt-1"><input type="number" min="0" inputMode="numeric" disabled={Number(graceValue || 0) <= 1} className="w-full p-2 rounded-l border border-white/10 disabled:opacity-45" style={inputStyle} value={Number(graceValue || 0) > 1 ? repeatValue : ''} onChange={(e) => {
              const raw = digitsOnly(e.target.value);
              const grace = Number(graceValue || 0);
              onPatch({ repeatEveryMins: raw && grace > 1 ? String(Math.min(Number(raw), grace - 1)) : '' });
            }} /><span className="px-3 py-2 rounded-r border border-l-0 border-white/10 bg-white/5">min</span></div>
          </label>
          <div className="rounded border border-white/10 p-2 bg-white/5 text-xs opacity-75">
            Escalation: <span className="font-semibold text-white">{escalationMethodLabel(defaults.escalationMethod || 'email')}</span>{defaults.escalateTo ? ` → ${defaults.escalateTo}` : ''}
            <div className="mt-1 opacity-70">Uses the standard Response Rules above. Grace expiry is exact; external delivery follows on Neo's next bounded response cycle.</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DutyModal({ dutyModal, setDutyModal, groups, roles = [], onSave }) {
  const form = dutyModal.form || {};
  const rule = normalizeResponseRule(form.responseRule);
  const patchForm = (patch) => setDutyModal(m => ({ ...m, form: { ...(m.form || {}), ...patch } }));
  const patchRule = (patch) => patchForm({ responseRule: { ...rule, ...patch } });
  return (
    <div className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4 neo-duty-modal" style={{ background: 'rgba(0,0,0,0.62)' }}>
      <div className="w-full max-w-3xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden text-white" style={{ background: '#0b1627' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10"><div><h2 className="text-xl font-semibold">{dutyModal.mode === 'edit' ? 'Edit Duty' : 'New Duty'}</h2><p className="text-sm opacity-65">Duty details and optional response alarm.</p></div><button className="text-2xl px-2" onClick={() => setDutyModal(null)}>×</button></div>
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <label className="text-sm opacity-90">Subheading<select className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={form.subheading || ''} onChange={e => patchForm({ subheading: e.target.value })}>{groups.map((g) => <option key={`${g.subheading}-${g.gi}`} value={g.subheading}>{g.subheading || 'General'}</option>)}{groups.length === 0 && <option value="General">General</option>}</select></label>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_170px] gap-3"><label className="text-sm opacity-90">Duty / Check Name *<input className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={form.label || ''} onChange={e => patchForm({ label: e.target.value })} placeholder="e.g. Lights switched on" /></label><label className="text-sm opacity-90">Type<select className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={form.type || 'checkbox'} onChange={e => patchForm({ type: e.target.value })}>{FIELD_TYPES.map(t => <option key={t} value={t} style={darkSelectOptionStyle}>{t}</option>)}</select></label></div>
          <div className="rounded-xl border border-cyan-500/20 p-4" style={{ background: 'rgba(34,211,238,0.05)' }}>
            <label className="text-sm opacity-90">Duty owner role
              <select className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={form.ownerRoleId || ''} onChange={e => { const role = roles.find(r => r.id === e.target.value); patchForm({ ownerRoleId: e.target.value, ownerRoleName: role?.name || '' }); }}>
                <option value="" style={darkSelectOptionStyle}>Any scheduled staff member</option>
                {roles.map(role => <option key={role.id} value={role.id} style={darkSelectOptionStyle}>{role.name || 'Unnamed role'}</option>)}
              </select>
            </label>
            <div className="mt-2 text-xs opacity-65">Neo resolves this role against the live clocked-in shift at the duty's store when the duty becomes due.</div>
            {form.ownerRoleId && <div className="mt-4 pt-4 border-t border-cyan-500/15 space-y-3">
              <div><div className="text-sm font-semibold">Responsibility Routing</div><div className="text-xs opacity-65 mt-1">Choose what Neo should do when nobody with the primary role is active.</div></div>
              <label className="text-sm opacity-90">If primary role is unavailable
                <select className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={form.responsibilityRouting?.mode || 'manager_decision'} onChange={e => patchForm({ responsibilityRouting: { ...(form.responsibilityRouting || {}), mode: e.target.value, fallbackRoles: form.responsibilityRouting?.fallbackRoles || [] } })}>
                  <option value="auto_assign" style={darkSelectOptionStyle}>Auto-assign first eligible fallback</option>
                  <option value="recommend" style={darkSelectOptionStyle}>Recommend fallback to manager</option>
                  <option value="manager_decision" style={darkSelectOptionStyle}>Manager must choose</option>
                </select>
              </label>
              <div>
                <div className="text-sm opacity-90 mb-2">Eligible fallback roles <span className="opacity-55">(in order)</span></div>
                <div className="flex flex-wrap gap-2">{roles.filter(role => role.id !== form.ownerRoleId).map(role => {
                  const selected = form.responsibilityRouting?.fallbackRoles || [];
                  const index = selected.findIndex(r => r.id === role.id);
                  const active = index >= 0;
                  return <button key={role.id} type="button" onClick={() => {
                    const next = active ? selected.filter(r => r.id !== role.id) : [...selected, { id: role.id, name: role.name || '' }];
                    patchForm({ responsibilityRouting: { ...(form.responsibilityRouting || {}), mode: form.responsibilityRouting?.mode || 'manager_decision', fallbackRoles: next } });
                  }} className={`px-3 py-2 rounded-lg border text-xs font-semibold ${active ? 'border-cyan-400/60 bg-cyan-400/15 text-cyan-200' : 'border-white/10 bg-white/5 text-white/70'}`}>{active ? `${index + 1}. ` : ''}{role.name || 'Unnamed role'}</button>;
                })}</div>
                <div className="text-xs opacity-55 mt-2">Neo never assigns an unrelated active person. Only the primary role or one of these eligible fallback roles can own this duty automatically.</div>
              </div>
            </div>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm opacity-90">Priority
              <select className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={form.priority || 'normal'} onChange={e => patchForm({ priority: e.target.value })}>
                <option value="low" style={darkSelectOptionStyle}>Low</option>
                <option value="normal" style={darkSelectOptionStyle}>Normal</option>
                <option value="high" style={darkSelectOptionStyle}>High</option>
                <option value="critical" style={darkSelectOptionStyle}>Critical</option>
              </select>
            </label>
            <label className="text-sm opacity-90">Recurrence
              <select className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={form.recurrence?.mode || 'daily'} onChange={e => patchForm({ recurrence: { ...(form.recurrence || {}), mode: e.target.value, days: e.target.value === 'selected_days' ? (form.recurrence?.days || []) : [] } })}>
                <option value="daily" style={darkSelectOptionStyle}>Every day</option>
                <option value="weekdays" style={darkSelectOptionStyle}>Weekdays</option>
                <option value="weekends" style={darkSelectOptionStyle}>Weekends</option>
                <option value="selected_days" style={darkSelectOptionStyle}>Selected days</option>
              </select>
            </label>
          </div>
          {(form.recurrence?.mode || 'daily') === 'selected_days' && <div className="rounded-xl border border-white/10 p-3">
            <div className="text-xs uppercase tracking-wider opacity-60 mb-2">Runs on</div>
            <div className="flex flex-wrap gap-2">{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(day => {
              const days = form.recurrence?.days || [];
              const active = days.includes(day);
              return <button key={day} type="button" onClick={() => patchForm({ recurrence: { mode: 'selected_days', days: active ? days.filter(d => d !== day) : [...days, day] } })} className={`px-3 py-2 rounded-lg border text-xs font-semibold ${active ? 'border-cyan-400/60 bg-cyan-400/15 text-cyan-200' : 'border-white/10 bg-white/5 text-white/70'}`}>{day}</button>;
            })}</div>
          </div>}
          <label className="text-sm opacity-90">Instructions (optional)<textarea className="w-full mt-1 p-3 rounded border border-white/10" rows={3} style={inputStyle} value={form.instructions || ''} onChange={e => patchForm({ instructions: e.target.value })} placeholder="Tell staff exactly what done means for this duty." /></label>
          <label className="flex items-center justify-between gap-3 rounded-xl border border-rose-400/20 p-4" style={{ background: 'rgba(244,63,94,0.08)' }}>
            <span>
              <span className="font-semibold">Critical duty</span>
              <span className="block text-xs opacity-65 mt-1">When this duty escalates, include it in Management chain levels marked Critical duties only.</span>
            </span>
            <input type="checkbox" checked={!!rule.criticalDuty} onChange={e => patchRule({ criticalDuty: e.target.checked })} />
          </label>
          <div className="rounded-xl border border-cyan-500/20 p-4" style={{ background: 'rgba(14,165,233,0.07)' }}>
            <label className="flex items-center justify-between gap-3"><span><span className="font-semibold">Duty Response Alarm</span><span className="block text-sm opacity-70">Optional. If off, this duty remains part of the standard but will not trigger reminders.</span></span><input type="checkbox" checked={!!rule.active} onChange={e => patchRule({ active: e.target.checked })} /></label>
            {rule.active && <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 text-sm">
                <label>Due time *<input type="time" className="w-full mt-1 p-3 rounded border border-white/10" style={inputStyle} value={rule.dueTime} onChange={e => patchRule({ dueTime: e.target.value })} /></label>
                <label>Reminder before
                  <div className="flex mt-1"><input type="number" min="0" inputMode="numeric" className="w-full p-3 rounded-l border border-white/10" style={inputStyle} value={rule.remindBeforeMins} onChange={e => patchRule({ remindBeforeMins: digitsOnly(e.target.value) })} /><span className="px-3 py-3 rounded-r border border-l-0 border-white/10 bg-white/5">min</span></div>
                </label>
                <label>Grace period
                  <div className="flex mt-1"><input type="number" min="0" inputMode="numeric" className="w-full p-3 rounded-l border border-white/10" style={inputStyle} value={rule.escalateAfterMins} onChange={e => {
                    const nextGrace = digitsOnly(e.target.value);
                    const graceValue = Number(nextGrace || 0);
                    const currentRepeat = Number(rule.repeatEveryMins || 0);
                    patchRule({
                      escalateAfterMins: nextGrace,
                      repeatEveryMins: graceValue > 1 && currentRepeat > 0
                        ? String(Math.min(currentRepeat, graceValue - 1))
                        : ''
                    });
                  }} /><span className="px-3 py-3 rounded-r border border-l-0 border-white/10 bg-white/5">min</span></div>
                </label>
                <label>Repeat during grace
                  <div className="flex mt-1">
                    <input
                      type="number"
                      min="1"
                      max={Math.max(1, Number(rule.escalateAfterMins || 0) - 1)}
                      inputMode="numeric"
                      disabled={Number(rule.escalateAfterMins || 0) <= 1}
                      className="w-full p-3 rounded-l border border-white/10 disabled:opacity-45 disabled:cursor-not-allowed"
                      style={inputStyle}
                      value={Number(rule.escalateAfterMins || 0) > 1 ? rule.repeatEveryMins : ''}
                      onChange={e => {
                        const raw = digitsOnly(e.target.value);
                        const graceValue = Number(rule.escalateAfterMins || 0);
                        if (!raw || graceValue <= 1) {
                          patchRule({ repeatEveryMins: '' });
                          return;
                        }
                        patchRule({ repeatEveryMins: String(Math.min(Number(raw), graceValue - 1)) });
                      }}
                      placeholder={Number(rule.escalateAfterMins || 0) > 1 ? `1-${Number(rule.escalateAfterMins) - 1}` : 'No grace'}
                    />
                    <span className="px-3 py-3 rounded-r border border-l-0 border-white/10 bg-white/5">min</span>
                  </div>
                  <span className="mt-1 block text-[11px] opacity-60">
                    {Number(rule.escalateAfterMins || 0) > 1
                      ? `Repeats only before escalation. Max ${Number(rule.escalateAfterMins) - 1} min.`
                      : 'No grace period = no repeat.'}
                  </span>
                </label>
              </div>

              <p className="mt-4 text-xs opacity-70">
                Escalation recipients and levels are managed in <span className="font-semibold">Management → Escalation Chain</span>.
              </p>
            </>}
          </div>
        </div>
        <div className="px-5 py-4 border-t border-white/10 flex items-center justify-end gap-3"><button className="px-4 py-2 rounded bg-white/10" onClick={() => setDutyModal(null)}>Close</button><button className="px-5 py-2 rounded font-semibold text-white" style={{ backgroundColor: 'var(--brand-accent)' }} onClick={onSave}>{dutyModal.mode === 'edit' ? 'Save Duty' : 'Add Duty'}</button></div>
      </div>
    </div>
  );
}

function LocalConfirm({ action, onCancel }) {
  return (
    <div className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.64)' }}>
      <div className="w-full max-w-md rounded-xl border border-white/10 p-5 text-white" style={{ background: '#0b1627' }}>
        <h2 className="text-xl font-semibold">{action.title}</h2>
        <p className="text-sm opacity-75 mt-2">{action.message}</p>
        <div className="flex justify-end gap-3 mt-5"><button className="px-4 py-2 rounded bg-white/10 disabled:opacity-50" disabled={!!action.busy} onClick={onCancel}>Cancel</button><button className={`px-4 py-2 rounded font-semibold disabled:opacity-60 ${action.danger ? 'bg-rose-600 text-white' : 'bg-cyan-600 text-white'}`} disabled={!!action.busy} onClick={action.onConfirm}>{action.okLabel || 'Confirm'}</button></div>
      </div>
    </div>
  );
}

function Step({ number, title, text, active, onClick }) {
  return <button type="button" onClick={onClick} className="w-full flex gap-3 text-left rounded-lg p-2 hover:bg-white/5"><div className={`w-8 h-8 rounded-full flex items-center justify-center border ${active ? 'bg-cyan-400 text-slate-950 border-cyan-300' : 'border-white/30 text-white/80'}`}>{number}</div><div><div className={`font-semibold ${active ? 'text-cyan-300' : ''}`}>{title}</div><div className="text-sm opacity-65 mt-1">{text}</div></div></button>;
}

function MetricBox({ label, value, ring }) {
  return <div className="min-w-[130px] rounded-lg border border-white/10 p-3" style={{ background: 'rgba(15,28,48,0.9)' }}><div className="text-sm opacity-65">{label}</div><div className="flex items-center gap-3"><div className="text-2xl font-bold">{value}</div>{ring && <div className="w-9 h-9 rounded-full border-4 border-cyan-400/80 border-t-emerald-400" />}</div></div>;
}
